import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import katex from "katex";
import {
  BookOpen,
  AlertTriangle,
  CheckSquare,
  Download,
  Eye,
  FileImage,
  FileText,
  FlaskConical,
  Loader2,
  Save,
  Settings2,
  ShieldCheck,
  Sparkles,
  Trash2,
  Wand2,
} from "lucide-react";
import { ApiService } from "../../services/api";
import {
  clearMaarifWizardPendingTask,
  clearMaarifWizardSession,
  getMaarifWizardSessionForRoute,
  persistMaarifWizardSessionOnLeave,
  restoreMaarifWizardSession,
  startMaarifWizardAuditTask,
  startMaarifWizardContextTask,
  startMaarifWizardImageTask,
  startMaarifWizardPdfTask,
  startMaarifWizardQuestionTask,
  startMaarifWizardRewriteTask,
  subscribeMaarifWizardSession,
  syncMaarifWizardSessionSnapshot,
  type MaarifWizardPendingTask,
  type MaarifWizardSessionState,
  type MaarifWizardSnapshot,
  type MaarifWizardTransientAssets,
} from "../../services/maarifQuestionWizardSession";
import PdfPageSelectModal from "../../components/modals/PdfPageSelectModal";
import { useModal } from "../../hooks/useModal";
import checkPermission, { checkSuperAdmin } from "../../utils/permission";
import type {
  MaarifQuestionBankExportInfo,
  MaarifQuestionDetectedMetadata,
  MaarifQuestionDifficulty,
  MaarifQuestionLengthPreset,
  MaarifQuestionAuditReport,
  MaarifPdfRewriteMode,
  MaarifQuestionPdfJobResponse,
  MaarifQuestionRecord,
  MaarifQuestionSourceAnalysis,
  MaarifQuestionImageAsset,
  MaarifSingleQuestion,
  MaarifWorkflowSource,
  MaarifPromptConfig,
} from "../../types/maarif-question-wizard";
import {
  MAARIF_DIFFICULTY_OPTIONS,
  MAARIF_GRADE_OPTIONS,
  MAARIF_LENGTH_OPTIONS,
  MAARIF_PROCESS_COMPONENTS,
} from "../../types/maarif-question-wizard";

type LessonOption = {
  id: string;
  title: string;
};

type QuestionMode = "context" | "image" | "pdf" | "pdf_contextual";
type WorkspacePanelTab = "workflow" | "settings" | "admin";

const choiceKeys: Array<keyof MaarifSingleQuestion["choices"]> = ["A", "B", "C", "D", "E"];
const MAX_PDF_PAGE_SELECTION = 30;
const MAARIF_QUESTION_PDF_SURFACE_CONVERT_PERMISSION = "maarif_question_pdf_surface_convert";
const MAARIF_QUESTION_PDF_CONTEXTUAL_CONVERT_PERMISSION = "maarif_question_pdf_contextual_convert";
const isPdfQuestionMode = (mode: QuestionMode) => mode === "pdf" || mode === "pdf_contextual";
const getPdfRewriteMode = (mode: QuestionMode): MaarifPdfRewriteMode => (mode === "pdf_contextual" ? "contextual" : "surface");
const getPdfModeTitle = (mode: QuestionMode) =>
  getPdfRewriteMode(mode) === "contextual" ? "PDF Çoklu Dönüşüm Akışı • Bağlamı Değiştir" : "PDF Çoklu Dönüşüm Akışı";
const getPdfModeDescription = (mode: QuestionMode) =>
  getPdfRewriteMode(mode) === "contextual"
    ? "Sistem PDF'teki soruları tek tek ayıklar, her sorunun kazanımını ve çözüm mantığını korur; ancak denklem, veri ve sayısal kurguyu değiştirerek görselsiz yeni varyantlar üretir."
    : "Sistem PDF'teki soruları tek tek ayıklar, her sorunun kavramını ve kazanımını korur, sonra denklemi bozmadan sayıları, örnekleri ve şıkları yenileyerek TYMM/Maarif uyumlu sürümler üretir.";
const getPdfModeActionLabel = (mode: QuestionMode) =>
  getPdfRewriteMode(mode) === "contextual" ? "PDF'ten Soruları Bağlamını Değiştirerek Çevir" : "PDF'ten Soruları Çevir";
const getPdfModePreviewTitle = (mode: QuestionMode) =>
  getPdfRewriteMode(mode) === "contextual" ? "Bağlamı Değiştirilen Sorular" : "Çevrilen Sorular";
const getPdfJobStageLabel = (job: MaarifQuestionPdfJobResponse | null) => {
  if (!job) return "PDF işleniyor";
  if (job.status === "queued") return "Kuyrukta bekliyor";
  if (job.status === "completed") return "Tamamlandı";
  if (job.status === "failed") return "Hata oluştu";

  switch (job.phase) {
    case "preparing_pdf":
      return "PDF hazırlanıyor";
    case "counting_questions":
      return "Soru sayısı belirleniyor";
    case "generating_questions":
      return "Sorular üretiliyor";
    case "validating_questions":
      return "Sorular doğrulanıyor";
    case "finalizing":
      return "Son düzenlemeler yapılıyor";
    default:
      return "PDF işleniyor";
  }
};
const getLengthDescription = (value: MaarifQuestionLengthPreset) =>
  MAARIF_LENGTH_OPTIONS.find((item) => item.value === value)?.description || "";

const buildSuggestedTitle = (lessonTitle: string, gainText: string) => {
  const lesson = String(lessonTitle || "").trim();
  const gain = String(gainText || "").trim();
  if (lesson && gain) return `${lesson} - ${gain}`;
  if (gain) return gain;
  if (lesson) return `${lesson} Soru Kaydı`;
  return "Maarif Soru Kaydı";
};

const formatQuestionForAudit = (question: MaarifSingleQuestion | null) => {
  if (!question) return "";
  return [
    `Ölçülen Süreç Bileşeni: ${question.measuredProcessComponentCode} - ${question.measuredProcessComponentLabel}`,
    question.image?.base64 ? "Destek Görseli: Var" : "Destek Görseli: Yok",
    "",
    "Bağlam:",
    question.context,
    "",
    "Soru Kökü:",
    question.questionStem,
    "",
    ...choiceKeys.map((key) => `${key}) ${question.choices[key]}`),
    "",
    `Doğru Cevap: ${question.correctAnswer || "-"}`,
    `Kısa Gerekçe: ${question.shortJustification}`,
  ].join("\n");
};

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString("tr-TR", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const getQuestionCollectionCount = (questions: MaarifSingleQuestion[] | null | undefined) =>
  Array.isArray(questions) ? questions.filter((item) => item?.questionStem).length : 0;

const getQuestionValidationWarnings = (question: MaarifSingleQuestion | null | undefined) =>
  Array.isArray(question?.validationWarnings) ? question.validationWarnings.filter((item) => item?.reason) : [];
const questionNeedsReview = (question: MaarifSingleQuestion | null | undefined) =>
  Boolean(question?.needsReview || question?.reviewStatus === "needs_review" || getQuestionValidationWarnings(question).length);

const getImageAssetSrc = (asset: MaarifQuestionImageAsset | null | undefined) => {
  const imageAsset = asset as (MaarifQuestionImageAsset & { dataUrl?: string; src?: string; url?: string }) | null | undefined;
  if (!imageAsset || typeof imageAsset !== "object") return "";

  if (typeof imageAsset.dataUrl === "string" && imageAsset.dataUrl.startsWith("data:")) return imageAsset.dataUrl;
  if (imageAsset.base64) return `data:${imageAsset.mimeType || "image/png"};base64,${imageAsset.base64}`;
  if (typeof imageAsset.src === "string" && imageAsset.src.trim()) return imageAsset.src.trim();
  if (typeof imageAsset.url === "string" && imageAsset.url.trim()) return imageAsset.url.trim();
  return "";
};

const getImageAssetKey = (asset: MaarifQuestionImageAsset | null | undefined) => {
  const src = getImageAssetSrc(asset);
  if (!src) return "";
  if (asset?.base64) return `${asset.mimeType || "image/png"}:${asset.base64.length}:${asset.base64.slice(0, 80)}`;
  return src;
};

const pushUniqueImageAsset = (
  assets: MaarifQuestionImageAsset[],
  seenKeys: Set<string>,
  asset: MaarifQuestionImageAsset | null | undefined
) => {
  const key = getImageAssetKey(asset);
  if (!key || seenKeys.has(key) || !asset) return;
  seenKeys.add(key);
  assets.push(asset);
};

const getImageAssetList = (value: unknown): MaarifQuestionImageAsset[] =>
  Array.isArray(value)
    ? value.filter((item): item is MaarifQuestionImageAsset => Boolean(getImageAssetSrc(item as MaarifQuestionImageAsset)))
    : [];

const getRichContentImageAssets = (document: unknown): MaarifQuestionImageAsset[] => {
  const blocks = (document as { blocks?: Array<{ type?: string; image?: MaarifQuestionImageAsset | null }> } | null)?.blocks;
  if (!Array.isArray(blocks)) return [];

  return blocks
    .filter((block) => block?.type === "image" && Boolean(getImageAssetSrc(block.image)))
    .map((block) => block.image as MaarifQuestionImageAsset);
};

const getQuestionImageAssets = (question: MaarifSingleQuestion | null) => {
  const assets: MaarifQuestionImageAsset[] = [];
  const seenKeys = new Set<string>();
  if (!question) return assets;

  const flexibleQuestion = question as MaarifSingleQuestion & {
    questionContent?: unknown;
    questionImages?: MaarifQuestionImageAsset[] | null;
  };

  pushUniqueImageAsset(assets, seenKeys, question.image);
  if (assets.length) {
    return assets.slice(0, 1);
  }

  const fallbackAssets = [
    ...getImageAssetList(question.bodyImages),
    ...getImageAssetList(question.images),
    ...getImageAssetList(question.supportImages),
    ...getImageAssetList(flexibleQuestion.questionImages),
    ...getRichContentImageAssets(flexibleQuestion.questionContent),
  ];
  if (fallbackAssets.length) {
    pushUniqueImageAsset(assets, seenKeys, fallbackAssets[0]);
  }

  return assets;
};

const getQuestionImageSrc = (question: MaarifSingleQuestion | null) => {
  return getImageAssetSrc(getQuestionImageAssets(question)[0]);
};

const getChoiceImageAssets = (question: MaarifSingleQuestion | null, choiceKey: keyof MaarifSingleQuestion["choices"]) => {
  const assets: MaarifQuestionImageAsset[] = [];
  const seenKeys = new Set<string>();
  if (!question) return assets;

  const flexibleQuestion = question as MaarifSingleQuestion & {
    choiceContents?: Partial<Record<keyof MaarifSingleQuestion["choices"], unknown>>;
  };

  pushUniqueImageAsset(assets, seenKeys, question.choiceImages?.[choiceKey]);
  getRichContentImageAssets(flexibleQuestion.choiceContents?.[choiceKey]).forEach((asset) =>
    pushUniqueImageAsset(assets, seenKeys, asset)
  );

  return assets;
};

const getChoiceImageSrc = (question: MaarifSingleQuestion | null, choiceKey: keyof MaarifSingleQuestion["choices"]) => {
  return getImageAssetSrc(getChoiceImageAssets(question, choiceKey)[0]);
};

const getQuestionChoiceLayout = (question: MaarifSingleQuestion | null) => {
  if (!question) return "stacked" as const;

  const hasChoiceImages = choiceKeys.some((key) => Boolean(getChoiceImageSrc(question, key)));
  if (hasChoiceImages) return "stacked" as const;

  const normalizedChoices = choiceKeys.map((key) => String(question.choices[key] || "").replace(/\s+/g, " ").trim());
  const maxLength = Math.max(...normalizedChoices.map((item) => item.length), 0);
  const totalLength = normalizedChoices.reduce((sum, item) => sum + item.length, 0);

  if (maxLength <= 18 && totalLength <= 90) return "five-column" as const;
  if (maxLength <= 64 && totalLength <= 220) return "two-column" as const;
  return "stacked" as const;
};

const questionHasRenderableImages = (question: MaarifSingleQuestion | null) =>
  Boolean(question && (getQuestionImageAssets(question).length || choiceKeys.some((key) => getChoiceImageAssets(question, key).length)));

type RichTextSegment = {
  type: "text" | "math" | "underline";
  content: string;
  displayMode: boolean;
};

const escapeHtml = (value: string) =>
  String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const normalizeScriptGrouping = (value: string) =>
  String(value || "").replace(/([_^])\s*(\(+)\s*([A-Za-z0-9+\-=]+)\s*(\)+)/g, (_match, marker, _open, inner) => {
    const normalizedInner = String(inner || "").trim();
    if (!normalizedInner) return marker;
    return /^[A-Za-z0-9]+$/.test(normalizedInner)
      ? `${marker}${normalizedInner}`
      : `${marker}(${normalizedInner})`;
  });

const normalizeLogicOperators = (value: string) =>
  String(value || "")
    .replace(/([A-Za-z0-9)])\s*\\?(?:Rightarrow|implies)\s*([A-Za-z0-9(])/g, "$1 ⇒ $2")
    .replace(/([A-Za-z0-9)])\s*\\?(?:Leftrightarrow|iff)\s*([A-Za-z0-9(])/g, "$1 ⇔ $2")
    .replace(/([A-Za-z0-9)])\s*\\?(?:vee|lor)\s*([A-Za-z0-9(])/g, "$1 ∨ $2")
    .replace(/([A-Za-z0-9)])\s*\\?(?:wedge|land)\s*([A-Za-z0-9(])/g, "$1 ∧ $2")
    .replace(/\\?neg\s*([A-Za-z0-9(])/g, "¬$1");

const normalizeGeneratedQuestionLayout = (value: string) => {
  let normalized = normalizeLogicOperators(normalizeScriptGrouping(String(value || "")))
    .replace(/&lt;br\s*\/?&gt;/gi, "\n")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/\\n(?![A-Za-z])/g, "\n")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .replace(/[ \t]*\n[ \t]*/g, "\n")
    .replace(/(^|[^\n])\s*(?:\\bullet|•)\s+/g, (_match, prefix) => `${prefix}\n• `)
    .replace(/\b(Buna göre)\s+(I{1,3}|IV|V|VI|VII|VIII|IX|X)\.\s+/gi, (_match, prefix, numeral) => `${prefix}\n${numeral}. `)
    .replace(/([?:;])\s+(I{1,3}|IV|V|VI|VII|VIII|IX|X)\.\s+/g, (_match, prefix, numeral) => `${prefix}\n${numeral}. `)
    .replace(/([a-zçğıöşü0-9])\.\s+(II|III|IV|V|VI|VII|VIII|IX|X)\.\s+/g, (_match, prefix, numeral) => `${prefix}.\n${numeral}. `)
    .replace(/\n{3,}/g, "\n\n");

  normalized = normalized
    .split("\n")
    .map((line) => line.trim())
    .join("\n")
    .trim();

  return normalized;
};

const isPlainMathTokenChar = (char: string) => /[A-Za-z0-9πθαβγλμσ∞⁰¹²³⁴⁵⁶⁷⁸⁹₀₁₂₃₄₅₆₇₈₉]/u.test(char);

const findPlainFractionOpenParenthesis = (source: string, closeIndex: number) => {
  let depth = 0;
  for (let index = closeIndex; index >= 0; index -= 1) {
    if (source[index] === ")") depth += 1;
    else if (source[index] === "(") {
      depth -= 1;
      if (depth === 0) return index;
    }
  }
  return -1;
};

const findPlainFractionCloseParenthesis = (source: string, openIndex: number) => {
  let depth = 0;
  for (let index = openIndex; index < source.length; index += 1) {
    if (source[index] === "(") depth += 1;
    else if (source[index] === ")") {
      depth -= 1;
      if (depth === 0) return index;
    }
  }
  return -1;
};

const stripPlainFractionParentheses = (value: string) => {
  let normalized = String(value || "").trim();
  let changed = true;

  while (changed && normalized.startsWith("(") && normalized.endsWith(")")) {
    changed = false;
    const closeIndex = findPlainFractionCloseParenthesis(normalized, 0);
    if (closeIndex === normalized.length - 1) {
      normalized = normalized.slice(1, -1).trim();
      changed = true;
    }
  }

  return normalized;
};

const readPlainFractionNumerator = (source: string, slashIndex: number) => {
  let end = slashIndex;
  while (end > 0 && /\s/u.test(source[end - 1])) end -= 1;
  if (end <= 0) return null;

  let start = end;
  if (source[end - 1] === ")") {
    const openIndex = findPlainFractionOpenParenthesis(source, end - 1);
    if (openIndex === -1) return null;
    start = openIndex;
    if (source[start - 1] === "-") start -= 1;
  } else {
    while (start > 0 && isPlainMathTokenChar(source[start - 1])) start -= 1;
    if (source[start - 1] === "-") start -= 1;
  }

  const value = source.slice(start, end).trim();
  return value ? { start, end, value } : null;
};

const readPlainFractionDenominator = (source: string, slashIndex: number) => {
  let start = slashIndex + 1;
  while (start < source.length && /\s/u.test(source[start])) start += 1;
  if (start >= source.length) return null;

  let end = start;
  if (source[start] === "(") {
    const closeIndex = findPlainFractionCloseParenthesis(source, start);
    if (closeIndex === -1) return null;
    end = closeIndex + 1;
  } else {
    if (source[end] === "-") end += 1;
    while (end < source.length && isPlainMathTokenChar(source[end])) end += 1;
  }

  const value = source.slice(start, end).trim();
  return value ? { start, end, value } : null;
};

const rewritePlainSlashFractions = (value: string, formatter: (numerator: string, denominator: string) => string) => {
  const source = String(value || "");
  if (!source.includes("/")) return source;

  let result = "";
  let cursor = 0;

  for (let index = 0; index < source.length; index += 1) {
    if (source[index] !== "/" || source[index - 1] === "/" || source[index + 1] === "/") continue;

    const numerator = readPlainFractionNumerator(source, index);
    const denominator = readPlainFractionDenominator(source, index);
    if (!numerator || !denominator || numerator.start < cursor) continue;

    const raw = source.slice(numerator.start, denominator.end);
    if (!/[A-Za-z0-9()+\-−=]/u.test(raw)) continue;

    result += source.slice(cursor, numerator.start);
    result += formatter(stripPlainFractionParentheses(numerator.value), stripPlainFractionParentheses(denominator.value));
    cursor = denominator.end;
    index = denominator.end - 1;
  }

  return result + source.slice(cursor);
};

const normalizePlainMathExpressionToLatex = (value: string) => {
  let normalized = String(value || "")
    .replace(/[−–]/g, "-")
    .replace(/\s+/g, " ")
    .trim();

  normalized = rewritePlainSlashFractions(normalized, (numerator, denominator) => `\\frac{${numerator}}{${denominator}}`);

  if (!normalized || /\\frac|\\sqrt|\\left|\\right/.test(normalized)) {
    return normalized;
  }

  const replaceNested = (pattern: RegExp, replacer: (...args: string[]) => string) => {
    let previous = "";
    while (previous !== normalized) {
      previous = normalized;
      normalized = normalized.replace(pattern, (...args) => replacer(...(args.slice(1, -2) as string[])));
    }
  };

  replaceNested(/-\(([^()]+)\)\s*\/\s*\(([^()]+)\)/g, (numerator, denominator) => `-\\frac{${numerator.trim()}}{${denominator.trim()}}`);
  replaceNested(/-\(([^()]+)\)\s*\/\s*(-?[A-Za-z0-9]+)/g, (numerator, denominator) => `-\\frac{${numerator.trim()}}{${denominator.trim()}}`);
  replaceNested(/\(([^()]+)\)\s*\/\s*\(([^()]+)\)/g, (numerator, denominator) => `\\frac{${numerator.trim()}}{${denominator.trim()}}`);
  replaceNested(/\(([^()]+)\)\s*\/\s*(-?[A-Za-z0-9]+)/g, (numerator, denominator) => `\\frac{${numerator.trim()}}{${denominator.trim()}}`);
  replaceNested(/(-?[A-Za-z0-9]+)\s*\/\s*\(([^()]+)\)/g, (numerator, denominator) => `\\frac{${numerator.trim()}}{${denominator.trim()}}`);
  replaceNested(
    /(^|[=+\-*/( ])(-?[A-Za-z0-9]+)\s*\/\s*(-?[A-Za-z0-9]+)(?=$|[=+\-*/).,;:!? ])/g,
    (prefix, numerator, denominator) => `${prefix}\\frac{${numerator.trim()}}{${denominator.trim()}}`
  );

  return normalized.replace(/\s+/g, " ").trim();
};

const canonicalizeLatexCommandSlashes = (value: string) => String(value || "").replace(/\\{2,}(?=[A-Za-z])/g, "\\");

const normalizeLatexCommandAliases = (value: string) =>
  canonicalizeLatexCommandSlashes(String(value || ""))
    .replace(/\\ge\b/g, "\\geq")
    .replace(/\\le\b/g, "\\leq")
    .replace(/\\ne\b/g, "\\neq")
    .replace(/\\to\b/g, "\\rightarrow")
    .replace(/\\Rightarrow\b/g, "\\implies")
    .replace(/\\Leftrightarrow\b/g, "\\iff")
    .replace(/\\vee\b/g, "∨")
    .replace(/\\lor\b/g, "∨")
    .replace(/\\wedge\b/g, "∧")
    .replace(/\\land\b/g, "∧")
    .replace(/\\neg\b/g, "¬");

const skipMathWhitespace = (source: string, startIndex: number) => {
  let index = startIndex;
  while (index < source.length && /\s/u.test(source[index])) {
    index += 1;
  }
  return index;
};

const readBalancedMathFragment = (source: string, startIndex: number, openChar: string, closeChar: string) => {
  if (source[startIndex] !== openChar) return null;

  let depth = 0;
  for (let index = startIndex; index < source.length; index += 1) {
    const char = source[index];
    if (char === openChar) depth += 1;
    else if (char === closeChar) {
      depth -= 1;
      if (depth === 0) {
        return {
          value: source.slice(startIndex, index + 1),
          endIndex: index + 1,
        };
      }
    }
  }

  return null;
};

const normalizeLatexFractionOperand = (value: string) => {
  const normalized = canonicalizeLatexCommandSlashes(String(value || "").trim());
  if (!normalized) return normalized;

  if (
    (normalized.startsWith("{") && normalized.endsWith("}")) ||
    (normalized.startsWith("[") && normalized.endsWith("]"))
  ) {
    return normalizeLatexFractionOperand(normalized.slice(1, -1));
  }

  const groupedRootMatch = normalized.match(/^([√∛∜])\((.*)\)$/u);
  if (groupedRootMatch) {
    const [, rootSymbol, radicand] = groupedRootMatch;
    const rootCommand = rootSymbol === "∛" ? "\\sqrt[3]" : rootSymbol === "∜" ? "\\sqrt[4]" : "\\sqrt";
    return `${rootCommand}{${radicand.trim()}}`;
  }

  const bareRootMatch = normalized.match(/^([√∛∜])([A-Za-z0-9]+)$/u);
  if (bareRootMatch) {
    const [, rootSymbol, radicand] = bareRootMatch;
    const rootCommand = rootSymbol === "∛" ? "\\sqrt[3]" : rootSymbol === "∜" ? "\\sqrt[4]" : "\\sqrt";
    return `${rootCommand}{${radicand.trim()}}`;
  }

  return normalized;
};

const readLatexFractionOperand = (source: string, startIndex: number) => {
  const index = skipMathWhitespace(source, startIndex);
  if (index >= source.length) return null;

  const char = source[index];

  if (char === "{" || char === "(" || char === "[") {
    const closeChar = char === "{" ? "}" : char === "(" ? ")" : "]";
    return readBalancedMathFragment(source, index, char, closeChar);
  }

  if (char === "√" || char === "∛" || char === "∜") {
    const groupedRadicand = source[index + 1] === "(" ? readBalancedMathFragment(source, index + 1, "(", ")") : null;
    if (groupedRadicand) {
      return {
        value: source.slice(index, groupedRadicand.endIndex),
        endIndex: groupedRadicand.endIndex,
      };
    }

    const bareRadicand = source.slice(index + 1).match(/^[A-Za-z0-9]+/u);
    if (bareRadicand) {
      return {
        value: `${char}${bareRadicand[0]}`,
        endIndex: index + 1 + bareRadicand[0].length,
      };
    }
  }

  if (char === "\\") {
    const commandMatch = source.slice(index).match(/^\\[A-Za-z]+/);
    if (!commandMatch) return null;

    let endIndex = index + commandMatch[0].length;
    endIndex = skipMathWhitespace(source, endIndex);

    if (source[endIndex] === "[") {
      const optionalArg = readBalancedMathFragment(source, endIndex, "[", "]");
      if (optionalArg) {
        endIndex = skipMathWhitespace(source, optionalArg.endIndex);
      }
    }

    if (source[endIndex] === "{" || source[endIndex] === "(") {
      const groupedArg = readBalancedMathFragment(source, endIndex, source[endIndex], source[endIndex] === "{" ? "}" : ")");
      if (groupedArg) {
        endIndex = groupedArg.endIndex;
      }
    }

    return {
      value: source.slice(index, endIndex),
      endIndex,
    };
  }

  const tokenMatch = source.slice(index).match(/^[+\-−]?(?:\d+(?:[.,]\d+)?|[A-Za-z][A-Za-z0-9]*|\d+[A-Za-z]+)(?:\^[+\-−]?\d+|[⁺⁻²³⁴⁵⁶⁷⁸⁹⁰]+)?/u);
  if (!tokenMatch) return null;

  return {
    value: tokenMatch[0],
    endIndex: index + tokenMatch[0].length,
  };
};

const rewriteLatexFractions = (value: string, formatter: (numerator: string, denominator: string) => string) => {
  const source = canonicalizeLatexCommandSlashes(String(value || ""));
  if (!source.includes("\\frac")) return source;

  let result = "";
  let cursor = 0;

  while (cursor < source.length) {
    const fracIndex = source.indexOf("\\frac", cursor);
    if (fracIndex === -1) {
      result += source.slice(cursor);
      break;
    }

    result += source.slice(cursor, fracIndex);

    const numerator = readLatexFractionOperand(source, fracIndex + "\\frac".length);
    if (!numerator) {
      result += "\\frac";
      cursor = fracIndex + "\\frac".length;
      continue;
    }

    const denominator = readLatexFractionOperand(source, numerator.endIndex);
    if (!denominator) {
      result += "\\frac";
      cursor = fracIndex + "\\frac".length;
      continue;
    }

    result += formatter(
      normalizeLatexFractionOperand(numerator.value),
      normalizeLatexFractionOperand(denominator.value)
    );
    cursor = denominator.endIndex;
  }

  return result;
};

const normalizeLooseLatexSyntax = (value: string) => {
  let normalized = normalizeLatexCommandAliases(String(value || ""))
    .replace(/\\([NQRZC])(?=(?:\^\{?[+\-−]?\}?|[^A-Za-z]|$))/g, "\\mathbb{$1}")
    .replace(/\\mathbb\s*([NQRZC])/g, "\\mathbb{$1}")
    .replace(/\\mathrm\s*([A-Za-z0-9])/g, "\\mathrm{$1}")
    .replace(/\\mathbf\s*([A-Za-z0-9])/g, "\\mathbf{$1}")
    .replace(/\\mathit\s*([A-Za-z0-9])/g, "\\mathit{$1}")
    .replace(/\\bigcirc/g, "○")
    .replace(/\\square/g, "□")
    .replace(/\\triangle/g, "△")
    .replace(/\\sqrt\(([^()\n]+)\)/g, "\\sqrt{$1}");

  normalized = rewriteLatexFractions(normalized, (numerator, denominator) => `\\frac{${numerator}}{${denominator}}`);
  return normalized;
};

const wrapBareLatexFragments = (value: string) => {
  let normalized = normalizeLooseLatexSyntax(String(value || ""));
  if (!normalized || normalized.includes("$")) return normalized;

  const wrapNested = (pattern: RegExp) => {
    let previous = "";
    while (previous !== normalized) {
      previous = normalized;
      normalized = normalized.replace(pattern, (match, prefix, fragment) => `${prefix}$${fragment}$`);
    }
  };

  wrapNested(
    /(^|[\s(=:;,+\-])((?:(?:\\frac\{[^{}]+\}\{[^{}]+\}|\\sqrt(?:\[[^\]]+\])?\{[^{}]+\}|\\[A-Za-z]+\{[^{}]*\}|\\[A-Za-z]+)(?:\^\{?[^{}\s]+\}?|_\{?[^{}\s]+\}?)*))(?=$|[\s).,;:!?=+\-])/g
  );

  return normalized;
};

const wrapPlainMathRuns = (value: string) => {
  const normalizedLayout = normalizeGeneratedQuestionLayout(String(value || ""));
  const normalizedFractions = rewritePlainSlashFractions(normalizedLayout, (numerator, denominator) => `\\frac{${numerator}}{${denominator}}`);
  const source = wrapBareLatexFragments(normalizeLooseLatexSyntax(normalizedFractions));
  if (!source) return source;

  let normalized = source;

  normalized = normalized.replace(
    /(^|[\s:(])((?:-?(?:\d+[A-Za-z]?|[A-Za-z](?:\([^()]+\))?|\([^()]+\))\s*(?:[+\-*/]\s*-?(?:\d+[A-Za-z]?|[A-Za-z](?:\([^()]+\))?|\([^()]+\))\s*)*)[=<>≤≥](?:\s*-?(?:\d+[A-Za-z]?|[A-Za-z](?:\([^()]+\))?|\([^()]+\))\s*(?:[+\-*/]\s*-?(?:\d+[A-Za-z]?|[A-Za-z](?:\([^()]+\))?|\([^()]+\))\s*)*))(?=$|[\s).,;:!?])/g,
    (match, prefix, expression) => `${prefix}$${normalizePlainMathExpressionToLatex(expression)}$`
  );

  normalized = normalized.replace(
    /(^|[\s:(])(-?(?:\([^()]+\)|[A-Za-z0-9]+)\s*\/\s*(?:\([^()]+\)|[A-Za-z0-9]+))(?=$|[\s).,;:!?])/g,
    (match, prefix, expression) => `${prefix}$${normalizePlainMathExpressionToLatex(expression)}$`
  );

  // Handle fractions where the text already has some $...$ but slash notation remains unwrapped
  // e.g. "x = 5 - 4n/(m + n)" inside a sentence that has other $...$ variables
  normalized = normalized.replace(
    /(^|[\s(=])(-?[A-Za-z0-9]+(?:\^[{]?[A-Za-z0-9]+[}]?)?\s*\/\s*\([^()]+\))(?=$|[\s).,;:!?])/g,
    (_match, prefix, expression) => {
      if (expression.includes("$")) return _match;
      return `${prefix}$${normalizePlainMathExpressionToLatex(expression)}$`;
    }
  );

  normalized = normalized.replace(
    /(^|[\s:(])((?:[A-Za-z0-9]+|\([^()\n]+\))(?:\^\{?[A-Za-z0-9()+\-=]+\}?)(?:\s*[+\-]\s*(?:[A-Za-z0-9]+|\([^()\n]+\))(?:\^\{?[A-Za-z0-9()+\-=]+\}?))+)(?=$|[\s).,;:!?])/g,
    (_match, prefix, expression) => {
      if (expression.includes("$")) return _match;
      return `${prefix}$${normalizePlainMathExpressionToLatex(expression)}$`;
    }
  );

  return normalized;
};

const parseRichTextSegments = (value: string): RichTextSegment[] => {
  const source = wrapPlainMathRuns(normalizeLooseLatexSyntax(normalizeGeneratedQuestionLayout(String(value || ""))));
  if (!source) return [];

  const segments: RichTextSegment[] = [];
  let buffer = "";
  let index = 0;

  while (index < source.length) {
    // Handle <u>...</u> markers (new format)
    if (source.startsWith("<u>", index)) {
      const closeTag = source.indexOf("</u>", index + 3);
      if (closeTag !== -1) {
        if (buffer) { segments.push({ type: "text", content: buffer, displayMode: false }); buffer = ""; }
        const underlineContent = source.slice(index + 3, closeTag).trim();
        if (underlineContent) segments.push({ type: "underline", content: underlineContent, displayMode: false });
        index = closeTag + 4;
        continue;
      }
    }

    // Handle __underline__ markers (eski format + geriye dönük uyum)
    if (source[index] === "_" && source[index + 1] === "_") {
      const startIndex = index + 2;
      const endIndex = source.indexOf("__", startIndex);
      if (endIndex !== -1) {
        if (buffer) {
          segments.push({ type: "text", content: buffer, displayMode: false });
          buffer = "";
        }
        let underlineContent = source.slice(startIndex, endIndex);
        // Bozuk parantezleri temizle: (((en)))) yüksek → en yüksek
        underlineContent = underlineContent.replace(/^\(+([^)]+?)\)+(?=\s|$)/g, "$1");
        underlineContent = underlineContent.replace(/^\(+([^)]+)\)+$/g, "$1");
        underlineContent = underlineContent.replace(/^\(+/, "").replace(/\)+$/, "").trim();
        if (underlineContent) {
          segments.push({ type: "underline", content: underlineContent, displayMode: false });
        }
        index = endIndex + 2;
        continue;
      }
    }

    if (source[index] !== "$") {
      buffer += source[index];
      index += 1;
      continue;
    }

    const isDisplayMode = source[index + 1] === "$";
    const delimiter = isDisplayMode ? "$$" : "$";
    const startIndex = index + delimiter.length;
    const endIndex = source.indexOf(delimiter, startIndex);

    if (endIndex === -1) {
      buffer += delimiter;
      index += delimiter.length;
      continue;
    }

    if (buffer) {
      segments.push({
        type: "text",
        content: buffer,
        displayMode: false,
      });
      buffer = "";
    }

    const mathContent = source.slice(startIndex, endIndex).trim();
    if (mathContent) {
      segments.push({
        type: "math",
        content: mathContent,
        displayMode: isDisplayMode,
      });
    }

    index = endIndex + delimiter.length;
  }

  if (buffer) {
    segments.push({
      type: "text",
      content: buffer,
      displayMode: false,
    });
  }

  return segments;
};

const hasMathContent = (value: string) => parseRichTextSegments(value).some((segment) => segment.type === "math");

const renderMathMarkup = (value: string, displayMode = false) => {
  try {
    return katex.renderToString(String(value || ""), {
      throwOnError: false,
      displayMode,
      output: "mathml",
    });
  } catch (error) {
    return `<span>${escapeHtml(String(value || ""))}</span>`;
  }
};

function RichTextContent({
  value,
  className = "",
  inline = false,
}: {
  value: string;
  className?: string;
  inline?: boolean;
}) {
  const segments = parseRichTextSegments(value);
  const RootElement = inline ? "span" : "div";
  const mergedClassName = `maarif-rich-text ${className}`.trim();

  if (!segments.length) {
    return <RootElement className={mergedClassName}>{value}</RootElement>;
  }

  return (
    <RootElement className={mergedClassName} style={{ whiteSpace: "pre-wrap" }}>
      <style>
        {`
          .maarif-rich-text .katex-display {
            overflow: visible;
            margin: 0.45rem 0;
            font-size: 1.32em;
          }
          .maarif-rich-text .katex {
            font-size: 1.22em;
            line-height: 1.35;
          }
        `}
      </style>
      {segments.map((segment, index) =>
        segment.type === "text" ? (
          <React.Fragment key={`text-${index}`}>{segment.content}</React.Fragment>
        ) : segment.type === "underline" ? (
          <span key={`underline-${index}`} style={{ textDecoration: "underline" }}>
            {segment.content}
          </span>
        ) : segment.displayMode ? (
          <div
            key={`math-${index}`}
            className="my-2"
            dangerouslySetInnerHTML={{ __html: renderMathMarkup(segment.content, true) }}
          />
        ) : (
          <span
            key={`math-${index}`}
            className="inline-block max-w-full align-middle"
            dangerouslySetInnerHTML={{ __html: renderMathMarkup(segment.content, false) }}
          />
        )
      )}
    </RootElement>
  );
}

const findMaarifProcessComponent = (code?: string | null, label?: string | null) => {
  const normalizedCode = String(code || "").trim();
  if (normalizedCode) {
    const matchedByCode = MAARIF_PROCESS_COMPONENTS.find((item) => item.code === normalizedCode);
    if (matchedByCode) return matchedByCode;
  }

  const normalizedLabel = String(label || "").trim().toLocaleLowerCase("tr-TR");
  if (normalizedLabel) {
    const matchedByLabel = MAARIF_PROCESS_COMPONENTS.find(
      (item) => item.label.trim().toLocaleLowerCase("tr-TR") === normalizedLabel
    );
    if (matchedByLabel) return matchedByLabel;
  }

  return null;
};

const normalizeQuestionProcessComponent = (
  targetQuestion: MaarifSingleQuestion | null,
  fallbackCode?: string | null
) => {
  if (!targetQuestion) return null;

  const fallbackComponent =
    findMaarifProcessComponent(fallbackCode || "", "") || MAARIF_PROCESS_COMPONENTS[0];
  const resolvedComponent =
    findMaarifProcessComponent(
      targetQuestion.measuredProcessComponentCode,
      targetQuestion.measuredProcessComponentLabel
    ) || fallbackComponent;

  return {
    ...targetQuestion,
    measuredProcessComponentCode: resolvedComponent.code,
    measuredProcessComponentLabel: resolvedComponent.label,
  };
};

const normalizeQuestionCollectionProcessComponents = (
  questions: MaarifSingleQuestion[] | null | undefined,
  fallbackCode?: string | null
) =>
  Array.isArray(questions)
    ? questions.map((item) => normalizeQuestionProcessComponent(item, fallbackCode)).filter(Boolean) as MaarifSingleQuestion[]
    : [];

const mapQuestionBankQuestionToMaarifQuestion = (
  question: any,
  fallbackCode?: string | null,
  fallbackLabel?: string | null,
  fallbackGainText?: string | null
): MaarifSingleQuestion => {
  const resolvedComponent =
    findMaarifProcessComponent(fallbackCode || "", fallbackLabel || "") || MAARIF_PROCESS_COMPONENTS[0];
  const choiceValues = question?.siklar || {};
  const choiceContents = question?.choiceContents || {};

  return {
    measuredProcessComponentCode: resolvedComponent.code,
    measuredProcessComponentLabel: resolvedComponent.label,
    gainText: fallbackGainText || null,
    sourcePageNumber: Number.isInteger(Number(question?.sira)) ? Number(question.sira) : null,
    context: "",
    questionStem: String(question?.soru_metni || ""),
    choices: {
      A: String(choiceValues.A || ""),
      B: String(choiceValues.B || ""),
      C: String(choiceValues.C || ""),
      D: String(choiceValues.D || ""),
      E: String(choiceValues.E || ""),
    },
    correctAnswer: choiceKeys.includes(question?.dogru_cevap) ? question.dogru_cevap : null,
    shortJustification: "",
    distractorAnalysis: { A: "", B: "", C: "", D: "", E: "" },
    image: question?.image || null,
    choiceImages: null,
    ...(question?.questionContent ? { questionContent: question.questionContent } : {}),
    ...(choiceContents ? { choiceContents } : {}),
  } as MaarifSingleQuestion;
};

const mapQuestionBankQuestionsToMaarifQuestions = (
  questionBank: any,
  fallbackCode?: string | null,
  fallbackLabel?: string | null,
  fallbackGainText?: string | null
): MaarifSingleQuestion[] =>
  Array.isArray(questionBank?.content?.sorular)
    ? questionBank.content.sorular.map((item: any) =>
        mapQuestionBankQuestionToMaarifQuestion(item, fallbackCode, fallbackLabel, fallbackGainText)
      )
    : [];

const mergeRenderableImagesIntoQuestion = (
  question: MaarifSingleQuestion,
  imageSource: MaarifSingleQuestion | null | undefined
) => {
  if (!imageSource) return question;

  const sourceWithRichContent = imageSource as MaarifSingleQuestion & {
    questionContent?: unknown;
    choiceContents?: unknown;
  };

  return {
    ...question,
    image: question.image?.base64 ? question.image : imageSource.image || question.image,
    bodyImages: question.bodyImages?.length ? question.bodyImages : imageSource.bodyImages || question.bodyImages,
    images: question.images?.length ? question.images : imageSource.images || question.images,
    supportImages: question.supportImages?.length ? question.supportImages : imageSource.supportImages || question.supportImages,
    choiceImages: question.choiceImages || imageSource.choiceImages || null,
    ...(sourceWithRichContent.questionContent ? { questionContent: sourceWithRichContent.questionContent } : {}),
    ...(sourceWithRichContent.choiceContents ? { choiceContents: sourceWithRichContent.choiceContents } : {}),
  } as MaarifSingleQuestion;
};

const PDF_CANVAS_WIDTH = 1240;
const PDF_CANVAS_HEIGHT = 1754;
const PDF_MARGIN_X = 88;
const PDF_TOP_MARGIN_Y = 72;
const PDF_BOTTOM_MARGIN_Y = 56;
const PDF_CONTENT_WIDTH = PDF_CANVAS_WIDTH - PDF_MARGIN_X * 2;
const PDF_PAGE_WIDTH_PT = 595.28;
const PDF_PAGE_HEIGHT_PT = 841.89;
const PDF_TEXT_FONT_FAMILY = "Arial, sans-serif";
const PDF_MATH_FONT_FAMILY = "\"Cambria Math\", \"STIX Two Math\", \"Times New Roman\", serif";
const PDF_MATH_FONT_SCALE = 1.12;
const PDF_INLINE_FRACTION_SCALE = 1.04;
const PDF_SUPERSCRIPT_PATTERN = /[⁰¹²³⁴⁵⁶⁷⁸⁹⁺⁻⁽⁾ⁿ]/u;
const PDF_TRAILING_PUNCTUATION = /[.,;:!?)\]»”’]+$/u;
const PDF_LEADING_PUNCTUATION = /^[([«“‘]+/u;

type PdfFormulaLayout =
  | { type: "text"; width: number; height: number; ascent: number; descent: number }
  | { type: "fraction"; width: number; height: number; ascent: number; descent: number; numerator: string; denominator: string }
  | {
      type: "root";
      width: number;
      height: number;
      ascent: number;
      descent: number;
      leadingText: string;
      radicand: string;
      indexText: string;
      trailingText: string;
    }
  | { type: "power"; width: number; height: number; ascent: number; descent: number; base: string; exponent: string }
  | { type: "math"; width: number; height: number; ascent: number; descent: number };

type PdfTokenLayout = {
  raw: string;
  prefix: string;
  core: string;
  suffix: string;
  width: number;
  height: number;
  ascent: number;
  descent: number;
  formula: PdfFormulaLayout;
};

type PdfLineLayout = {
  tokens: PdfTokenLayout[];
  height: number;
  ascent: number;
  descent: number;
  blank?: boolean;
};

type QuestionPdfMetadataItem = {
  label: string;
  value: string;
};

const buildCanvasFont = (size: number, weight = "normal", family = PDF_TEXT_FONT_FAMILY) =>
  `${weight} ${size}px ${family}`;

const measureCanvasTextMetrics = (
  ctx: CanvasRenderingContext2D,
  text: string,
  fallbackFontSize: number
) => {
  const metrics = ctx.measureText(text || " ");
  const ascent = metrics.actualBoundingBoxAscent || fallbackFontSize * 0.8;
  const descent = metrics.actualBoundingBoxDescent || fallbackFontSize * 0.26;

  return {
    width: metrics.width,
    ascent,
    descent,
    height: ascent + descent,
  };
};

const splitPdfTokenAffixes = (token: string) => {
  const normalized = String(token || "");
  const leading = normalized.match(PDF_LEADING_PUNCTUATION)?.[0] || "";
  const trailing = normalized.match(PDF_TRAILING_PUNCTUATION)?.[0] || "";
  const core = normalized.slice(leading.length, normalized.length - trailing.length) || normalized;

  return {
    prefix: leading,
    core,
    suffix: trailing,
  };
};

const isLikelyMathToken = (token: string) => {
  const normalized = String(token || "").trim();
  if (!normalized) return false;
  if (normalized.includes("√") || normalized.includes("∛") || normalized.includes("∜")) return true;
  if (normalized.includes("^")) return true;
  if (normalized.includes("π") || normalized.includes("∞")) return true;
  if (/[=+\-−*/()]/u.test(normalized) && /[A-Za-z0-9]/u.test(normalized)) return true;
  if (PDF_SUPERSCRIPT_PATTERN.test(normalized)) return true;
  if (/^[+\-−]?(?:\d+(?:[.,]\d+)?|[A-Za-z]+)\/(?:\d+(?:[.,]\d+)?|[A-Za-z]+)$/u.test(normalized)) return true;
  return false;
};

const splitTopLevelFraction = (token: string) => {
  const normalized = String(token || "").trim();
  if (!normalized || !normalized.includes("/")) return null;

  let depth = 0;
  for (let index = 0; index < normalized.length; index += 1) {
    const char = normalized[index];
    if (char === "(" || char === "[" || char === "{") depth += 1;
    else if (char === ")" || char === "]" || char === "}") depth = Math.max(0, depth - 1);
    else if (char === "/" && depth === 0) {
      const numerator = normalized.slice(0, index).trim();
      const denominator = normalized.slice(index + 1).trim();
      if (numerator && denominator && !denominator.includes("/")) {
        return { numerator, denominator };
      }
    }
  }

  return null;
};

const stripOuterGrouping = (value: string) => {
  const normalized = String(value || "").trim();
  if (!normalized) return normalized;
  if (
    (normalized.startsWith("(") && normalized.endsWith(")")) ||
    (normalized.startsWith("[") && normalized.endsWith("]")) ||
    (normalized.startsWith("{") && normalized.endsWith("}"))
  ) {
    return normalized.slice(1, -1).trim();
  }
  return normalized;
};

const isSimplePdfFractionPart = (value: string) => {
  const normalized = stripOuterGrouping(value);
  if (!normalized) return false;
  if (/\s/u.test(normalized)) return false;
  if (/[=+*^_]/u.test(normalized)) return false;
  if (normalized.slice(1).includes("-") || normalized.slice(1).includes("−")) return false;
  return true;
};

const formatPdfFraction = (numerator: string, denominator: string) => {
  const safeNumerator = stripOuterGrouping(numerator);
  const safeDenominator = stripOuterGrouping(denominator);
  const normalizedNumerator = isSimplePdfFractionPart(safeNumerator) ? safeNumerator : `(${safeNumerator})`;
  const normalizedDenominator = isSimplePdfFractionPart(safeDenominator) ? safeDenominator : `(${safeDenominator})`;
  return `${normalizedNumerator}∕${normalizedDenominator}`;
};

const parsePdfRootExpression = (value: string) => {
  const normalized = String(value || "").trim();
  if (!normalized || !normalized.includes("√") && !normalized.includes("∛") && !normalized.includes("∜")) {
    return null;
  }

  const match = normalized.match(/^(.*?)([²³⁴]?)([√∛∜])(?:\(([^()]+)\)|([A-Za-z0-9]+))(.*)$/u);
  if (!match) {
    return null;
  }

  const [, leadingText, rawIndexText, rootSymbol, groupedRadicand, bareRadicand, trailingText] = match;
  const indexText = rawIndexText || (rootSymbol === "∛" ? "3" : rootSymbol === "∜" ? "4" : "");
  const rawRadicand = (groupedRadicand || bareRadicand || "").trim();
  const radicand = groupedRadicand ? `(${rawRadicand})` : rawRadicand;

  if (!radicand) {
    return null;
  }

  return {
    leadingText: leadingText || "",
    indexText,
    radicand,
    trailingText: trailingText || "",
  };
};

const normalizeLatexMathForPdf = (value: string) => {
  let normalized = normalizeLatexCommandAliases(normalizeLooseLatexSyntax(String(value || "")));
  if (!normalized) return normalized;

  const replaceNestedLatex = (pattern: RegExp, replacer: (...args: string[]) => string) => {
    let previous = "";
    while (previous !== normalized) {
      previous = normalized;
      normalized = normalized.replace(pattern, (...args) => replacer(...(args.slice(1, -2) as string[])));
    }
  };

  normalized = normalized
    .replace(/\\mathbb\{N\}/g, "ℕ")
    .replace(/\\mathbb\{Z\}/g, "ℤ")
    .replace(/\\mathbb\{Q\}/g, "ℚ")
    .replace(/\\mathbb\{R\}/g, "ℝ")
    .replace(/\\mathbb\{C\}/g, "ℂ")
    .replace(/\\mathrm\{([^{}]*)\}/g, "$1")
    .replace(/\\mathbf\{([^{}]*)\}/g, "$1")
    .replace(/\\mathit\{([^{}]*)\}/g, "$1")
    .replace(/\\left/g, "")
    .replace(/\\right/g, "")
    .replace(/\\cdot/g, "·")
    .replace(/\\times/g, "×")
    .replace(/\\div/g, "÷")
    .replace(/\\leq/g, "≤")
    .replace(/\\geq/g, "≥")
    .replace(/\\Rightarrow/g, "⇒")
    .replace(/\\Leftrightarrow/g, "⇔")
    .replace(/\\rightarrow/g, "→")
    .replace(/\\leftarrow/g, "←")
    .replace(/\\implies/g, "⇒")
    .replace(/\\iff/g, "⇔")
    .replace(/\\vee/g, "∨")
    .replace(/\\lor/g, "∨")
    .replace(/\\wedge/g, "∧")
    .replace(/\\land/g, "∧")
    .replace(/\\neg/g, "¬")
    .replace(/\\subseteq/g, "⊆")
    .replace(/\\supseteq/g, "⊇")
    .replace(/\\subset/g, "⊂")
    .replace(/\\supset/g, "⊃")
    .replace(/\\cup/g, "∪")
    .replace(/\\cap/g, "∩")
    .replace(/\\neq/g, "≠")
    .replace(/\\approx/g, "≈")
    .replace(/\\in/g, "∈")
    .replace(/\\notin/g, "∉")
    .replace(/\\forall/g, "∀")
    .replace(/\\exists/g, "∃")
    .replace(/\\therefore/g, "∴")
    .replace(/\\because/g, "∵")
    .replace(/\\mapsto/g, "↦")
    .replace(/\\parallel/g, "∥")
    .replace(/\\perp/g, "⊥")
    .replace(/\\mid/g, "|")
    .replace(/\\infty/g, "∞")
    .replace(/\\pi/g, "π")
    .replace(/\\pm/g, "±")
    .replace(/\\mp/g, "∓")
    .replace(/\\bigcirc/g, "○")
    .replace(/\\square/g, "□")
    .replace(/\\triangle/g, "△")
    .replace(/\\alpha/g, "α")
    .replace(/\\beta/g, "β")
    .replace(/\\gamma/g, "γ")
    .replace(/\\theta/g, "θ")
    .replace(/\\lambda/g, "λ")
    .replace(/\\mu/g, "μ")
    .replace(/\\sigma/g, "σ")
    .replace(/\\Delta/g, "Δ")
    .replace(/\\degree/g, "°")
    .replace(/\\,/g, " ")
    .replace(/\\!/g, "")
    .replace(/\\;/g, " ")
    .replace(/\\:/g, " ")
    .replace(/\\quad/g, " ")
    .replace(/\\qquad/g, " ")
    .replace(/\\\\(?=\s|$)/g, "\n");

  replaceNestedLatex(/\\text\{([^{}]*)\}/g, (text) => text);
  replaceNestedLatex(/\\sqrt\[(.*?)\]\{([^{}]*)\}/g, (indexText, radicand) => `${indexText}√(${radicand})`);
  replaceNestedLatex(/\\sqrt\{([^{}]*)\}/g, (radicand) => `√(${radicand})`);

  normalized = rewriteLatexFractions(normalized, (numerator, denominator) =>
    formatPdfFraction(normalizeLatexMathForPdf(numerator), normalizeLatexMathForPdf(denominator))
  );

  normalized = normalized
    .replace(/\\frac\b/g, "")
    .replace(/\\([A-Za-z]+)\b/g, "$1")
    .replace(/\^\{([^{}]+)\}/g, "^$1")
    .replace(/_\{([^{}]+)\}/g, "_$1")
    .replace(/([ℕℤℚℝℂ])\^\+/g, "$1⁺")
    .replace(/([ℕℤℚℝℂ])\^-/g, "$1⁻")
    .replace(/[{}]/g, "")
    .replace(/\s+/g, " ")
    .trim();

  return normalized;
};

const normalizeRichTextForPdf = (value: string) =>
  parseRichTextSegments(value)
    .map((segment) => {
      if (segment.type === "text") {
        return /[\\√∛∜]/u.test(segment.content) ? normalizeLatexMathForPdf(segment.content) : segment.content;
      }
      const normalizedMath = normalizeLatexMathForPdf(segment.content);
      return segment.displayMode ? `\n${normalizedMath}\n` : normalizedMath;
    })
    .join("")
    .replace(/\n{3,}/g, "\n\n");

const measurePdfFormula = (
  ctx: CanvasRenderingContext2D,
  token: string,
  options?: { font?: string; lineHeight?: number }
): PdfFormulaLayout => {
  const font = options?.font || "18px Arial";
  const lineHeight = options?.lineHeight || 28;
  const fontSizeMatch = font.match(/(\d+(?:\.\d+)?)px/i);
  const baseFontSize = fontSizeMatch ? Number(fontSizeMatch[1]) : 18;
  const fontWeight = font.toLowerCase().includes("bold") ? "bold" : "normal";
  const normalized = String(token || "").trim();
  const mathFontSize = baseFontSize * PDF_MATH_FONT_SCALE;

  const setTextFont = () => {
    ctx.font = buildCanvasFont(baseFontSize, fontWeight, PDF_TEXT_FONT_FAMILY);
  };

  const setMathFont = (size = mathFontSize, weight = fontWeight) => {
    ctx.font = buildCanvasFont(size, weight, PDF_MATH_FONT_FAMILY);
  };

  const fractionParts = splitTopLevelFraction(normalized);
  if (fractionParts) {
    const numerator = stripOuterGrouping(fractionParts.numerator);
    const denominator = stripOuterGrouping(fractionParts.denominator);
    const fractionFontSize = Math.max(13, baseFontSize * PDF_INLINE_FRACTION_SCALE);
    setMathFont(fractionFontSize);
    const numeratorMetrics = measureCanvasTextMetrics(ctx, numerator, fractionFontSize);
    const denominatorMetrics = measureCanvasTextMetrics(ctx, denominator, fractionFontSize);
    const numeratorWidth = numeratorMetrics.width;
    const denominatorWidth = denominatorMetrics.width;
    const width = Math.max(numeratorWidth, denominatorWidth) + 14;
    const numeratorOffset = Math.max(8, baseFontSize * 0.42);
    const denominatorOffset = Math.max(14, baseFontSize * 0.7);
    const ascent = numeratorOffset + numeratorMetrics.ascent;
    const descent = denominatorOffset + denominatorMetrics.descent;
    const height = Math.max(lineHeight, Math.ceil(ascent + descent));
    return {
      type: "fraction",
      numerator,
      denominator,
      width,
      height,
      ascent,
      descent,
    };
  }

  const rootExpression = parsePdfRootExpression(normalized);
  if (rootExpression) {
    setTextFont();
    const rootTextMetrics = measureCanvasTextMetrics(ctx, normalized, baseFontSize);
    const extraLeading = Math.max(0, lineHeight - rootTextMetrics.height);
    const ascent = rootTextMetrics.ascent + extraLeading * 0.55;
    const descent = rootTextMetrics.descent + extraLeading * 0.45;
    return {
      type: "text",
      width: rootTextMetrics.width,
      height: Math.max(lineHeight, Math.ceil(ascent + descent)),
      ascent,
      descent,
    };
  }

  const powerMatch = normalized.match(/^(.+)\^([+\-−]?\d+)$/u);
  if (powerMatch) {
    const base = powerMatch[1];
    const exponent = powerMatch[2];
    setMathFont(mathFontSize);
    const baseMetrics = measureCanvasTextMetrics(ctx, base, mathFontSize);
    const exponentFontSize = Math.max(11, baseFontSize * 0.62);
    setMathFont(exponentFontSize);
    const exponentMetrics = measureCanvasTextMetrics(ctx, exponent, exponentFontSize);
    const exponentShift = Math.max(8, baseFontSize * 0.45);
    const ascent = Math.max(baseMetrics.ascent, exponentShift + exponentMetrics.ascent);
    const descent = Math.max(baseMetrics.descent, baseFontSize * 0.2);
    return {
      type: "power",
      base,
      exponent,
      width: baseMetrics.width + exponentMetrics.width + 2,
      height: Math.max(lineHeight, Math.ceil(ascent + descent)),
      ascent,
      descent,
    };
  }

  if (isLikelyMathToken(normalized)) {
    setMathFont(mathFontSize);
    const mathMetrics = measureCanvasTextMetrics(ctx, normalized, mathFontSize);
    const extraLeading = Math.max(0, lineHeight - mathMetrics.height);
    const ascent = mathMetrics.ascent + extraLeading * 0.55;
    const descent = mathMetrics.descent + extraLeading * 0.45;
    return {
      type: "math",
      width: mathMetrics.width,
      height: Math.max(lineHeight, Math.ceil(ascent + descent)),
      ascent,
      descent,
    };
  }

  setTextFont();
  const textMetrics = measureCanvasTextMetrics(ctx, normalized, baseFontSize);
  const extraLeading = Math.max(0, lineHeight - textMetrics.height);
  const ascent = textMetrics.ascent + extraLeading * 0.55;
  const descent = textMetrics.descent + extraLeading * 0.45;
  return {
    type: "text",
    width: textMetrics.width,
    height: Math.max(lineHeight, Math.ceil(ascent + descent)),
    ascent,
    descent,
  };
};

const buildPdfTokenLayout = (
  ctx: CanvasRenderingContext2D,
  token: string,
  options?: { font?: string; lineHeight?: number }
): PdfTokenLayout => {
  const font = options?.font || "18px Arial";
  const lineHeight = options?.lineHeight || 28;
  const fontSizeMatch = font.match(/(\d+(?:\.\d+)?)px/i);
  const baseFontSize = fontSizeMatch ? Number(fontSizeMatch[1]) : 18;
  const fontWeight = font.toLowerCase().includes("bold") ? "bold" : "normal";
  const { prefix, core: rawCore, suffix } = splitPdfTokenAffixes(token);
  const rawFractionParts = splitTopLevelFraction(rawCore);
  const core = rawFractionParts ? formatPdfFraction(rawFractionParts.numerator, rawFractionParts.denominator) : rawCore;

  ctx.font = buildCanvasFont(baseFontSize, fontWeight, PDF_TEXT_FONT_FAMILY);
  const prefixMetrics = prefix ? measureCanvasTextMetrics(ctx, prefix, baseFontSize) : null;
  const suffixMetrics = suffix ? measureCanvasTextMetrics(ctx, suffix, baseFontSize) : null;
  const prefixWidth = prefixMetrics?.width || 0;
  const suffixWidth = suffixMetrics?.width || 0;
  const formula = measurePdfFormula(ctx, core, { font, lineHeight });
  const ascent = Math.max(prefixMetrics?.ascent || 0, formula.ascent, suffixMetrics?.ascent || 0);
  const descent = Math.max(prefixMetrics?.descent || 0, formula.descent, suffixMetrics?.descent || 0);

  return {
    raw: token,
    prefix,
    core,
    suffix,
    width: prefixWidth + formula.width + suffixWidth,
    height: Math.max(lineHeight, Math.ceil(ascent + descent)),
    ascent,
    descent,
    formula,
  };
};

const buildPdfLineLayouts = (
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  options?: { font?: string; lineHeight?: number }
) => {
  const font = options?.font || "18px Arial";
  const lineHeight = options?.lineHeight || 28;
  const fontSizeMatch = font.match(/(\d+(?:\.\d+)?)px/i);
  const baseFontSize = fontSizeMatch ? Number(fontSizeMatch[1]) : 18;
  const fontWeight = font.toLowerCase().includes("bold") ? "bold" : "normal";
  ctx.font = buildCanvasFont(baseFontSize, fontWeight, PDF_TEXT_FONT_FAMILY);
  const spaceWidth = ctx.measureText(" ").width;

  const paragraphs = String(text || "").replace(/\r\n/g, "\n").split("\n");
  const lines: PdfLineLayout[] = [];

  paragraphs.forEach((paragraph, paragraphIndex) => {
    const trimmedParagraph = paragraph.trim();
    if (!trimmedParagraph) {
      const blankHeight = Math.max(24, Math.floor(lineHeight * 0.9));
      lines.push({ tokens: [], height: blankHeight, ascent: 0, descent: blankHeight, blank: true });
      return;
    }

    const words = trimmedParagraph.split(/\s+/).filter(Boolean);
    let currentTokens: PdfTokenLayout[] = [];
    let currentWidth = 0;
    let currentAscent = 0;
    let currentDescent = 0;

    words.forEach((word) => {
      const tokenLayout = buildPdfTokenLayout(ctx, word, { font, lineHeight });
      const candidateWidth = currentTokens.length
        ? currentWidth + spaceWidth + tokenLayout.width
        : tokenLayout.width;

      if (currentTokens.length && candidateWidth > maxWidth) {
        const extraLineGap = currentTokens.some((token) => token.formula.type !== "text")
          ? Math.max(8, Math.round(lineHeight * 0.28))
          : Math.max(2, Math.round(lineHeight * 0.1));
        lines.push({
          tokens: currentTokens,
          height: Math.max(lineHeight, Math.ceil(currentAscent + currentDescent)) + extraLineGap,
          ascent: currentAscent,
          descent: currentDescent,
        });
        currentTokens = [tokenLayout];
        currentWidth = tokenLayout.width;
        currentAscent = tokenLayout.ascent;
        currentDescent = tokenLayout.descent;
        return;
      }

      currentTokens.push(tokenLayout);
      currentWidth = candidateWidth;
      currentAscent = Math.max(currentAscent, tokenLayout.ascent);
      currentDescent = Math.max(currentDescent, tokenLayout.descent);
    });

    if (currentTokens.length) {
      const extraLineGap = currentTokens.some((token) => token.formula.type !== "text")
        ? Math.max(8, Math.round(lineHeight * 0.28))
        : Math.max(2, Math.round(lineHeight * 0.1));
      lines.push({
        tokens: currentTokens,
        height: Math.max(lineHeight, Math.ceil(currentAscent + currentDescent)) + extraLineGap,
        ascent: currentAscent,
        descent: currentDescent,
      });
    }

    if (paragraphIndex < paragraphs.length - 1 && trimmedParagraph) {
      const blankHeight = Math.max(18, Math.floor(lineHeight * 0.65));
      lines.push({ tokens: [], height: blankHeight, ascent: 0, descent: blankHeight, blank: true });
    }
  });

  const height = lines.reduce((total, line) => total + line.height, 0);
  return { lines, height };
};

const drawPdfFormulaToken = (
  ctx: CanvasRenderingContext2D,
  tokenLayout: PdfTokenLayout,
  x: number,
  baselineY: number,
  options?: { font?: string; color?: string }
) => {
  const font = options?.font || "18px Arial";
  const color = options?.color || "#1f2937";
  const fontSizeMatch = font.match(/(\d+(?:\.\d+)?)px/i);
  const baseFontSize = fontSizeMatch ? Number(fontSizeMatch[1]) : 18;
  const fontWeight = font.toLowerCase().includes("bold") ? "bold" : "normal";
  const textFont = buildCanvasFont(baseFontSize, fontWeight, PDF_TEXT_FONT_FAMILY);
  const mathFont = buildCanvasFont(baseFontSize * PDF_MATH_FONT_SCALE, fontWeight, PDF_MATH_FONT_FAMILY);

  ctx.textBaseline = "alphabetic";
  ctx.fillStyle = color;
  ctx.font = textFont;

  let currentX = x;
  if (tokenLayout.prefix) {
    ctx.fillText(tokenLayout.prefix, currentX, baselineY);
    currentX += ctx.measureText(tokenLayout.prefix).width;
  }

  const formula = tokenLayout.formula;
  if (formula.type === "fraction") {
    const fractionFont = buildCanvasFont(Math.max(13, baseFontSize * PDF_INLINE_FRACTION_SCALE), fontWeight, PDF_MATH_FONT_FAMILY);
    ctx.font = fractionFont;
    const numeratorWidth = ctx.measureText(formula.numerator).width;
    const denominatorWidth = ctx.measureText(formula.denominator).width;
    const innerWidth = Math.max(numeratorWidth, denominatorWidth);
    const blockWidth = formula.width;
    const startX = currentX + (blockWidth - innerWidth) / 2;
    const numeratorY = baselineY - Math.max(8, baseFontSize * 0.42);
    const denominatorY = baselineY + Math.max(14, baseFontSize * 0.7);

    ctx.fillText(formula.numerator, startX + (innerWidth - numeratorWidth) / 2, numeratorY);
    ctx.beginPath();
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.6;
    ctx.moveTo(currentX + 2, baselineY + 3);
    ctx.lineTo(currentX + blockWidth - 2, baselineY + 3);
    ctx.stroke();
    ctx.fillText(formula.denominator, startX + (innerWidth - denominatorWidth) / 2, denominatorY);
    currentX += blockWidth;
  } else if (formula.type === "root") {
    const mathTextFont = buildCanvasFont(baseFontSize * PDF_MATH_FONT_SCALE, fontWeight, PDF_MATH_FONT_FAMILY);
    const rootFont = buildCanvasFont(baseFontSize * 1.12 * PDF_MATH_FONT_SCALE, fontWeight, PDF_MATH_FONT_FAMILY);
    const indexFont = buildCanvasFont(Math.max(11, baseFontSize * 0.58), fontWeight, PDF_MATH_FONT_FAMILY);
    const indexLift = Math.max(8, baseFontSize * 0.46);
    const rootBarLift = Math.max(10, baseFontSize * 0.5);
    ctx.font = mathTextFont;
    if (formula.leadingText) {
      ctx.fillText(formula.leadingText, currentX, baselineY);
      currentX += ctx.measureText(formula.leadingText).width;
    }
    ctx.font = indexFont;
    const indexWidth = formula.indexText ? ctx.measureText(formula.indexText).width : 0;
    if (formula.indexText) {
      ctx.fillText(formula.indexText, currentX, baselineY - indexLift);
    }
    currentX += indexWidth;
    ctx.font = rootFont;
    const rootWidth = ctx.measureText("√").width;
    ctx.fillText("√", currentX, baselineY + 1);
    currentX += rootWidth - 1;
    ctx.font = mathTextFont;
    const radicandWidth = ctx.measureText(formula.radicand).width;
    ctx.beginPath();
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.2;
    ctx.moveTo(currentX - 2, baselineY - rootBarLift);
    ctx.lineTo(currentX + radicandWidth + 2, baselineY - rootBarLift);
    ctx.stroke();
    ctx.fillText(formula.radicand, currentX + 1, baselineY);
    currentX += radicandWidth + 4;
    if (formula.trailingText) {
      ctx.fillText(formula.trailingText, currentX, baselineY);
      currentX += ctx.measureText(formula.trailingText).width;
    }
  } else if (formula.type === "power") {
    ctx.font = mathFont;
    const baseWidth = ctx.measureText(formula.base).width;
    ctx.fillText(formula.base, currentX, baselineY);
    currentX += baseWidth;
    ctx.font = buildCanvasFont(Math.max(11, baseFontSize * 0.62), fontWeight, PDF_MATH_FONT_FAMILY);
    ctx.fillText(formula.exponent, currentX, baselineY - Math.max(8, baseFontSize * 0.45));
    currentX += ctx.measureText(formula.exponent).width + 2;
  } else {
    ctx.font = formula.type === "math" ? mathFont : textFont;
    ctx.fillText(tokenLayout.core, currentX, baselineY);
    currentX += formula.width;
  }

  if (tokenLayout.suffix) {
    ctx.font = textFont;
    ctx.fillText(tokenLayout.suffix, currentX, baselineY);
  }
};

const sanitizeFileName = (value: string) => {
  const normalized = String(value || "")
    .replace(/[<>:"/\\|?*\u0000-\u001F]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  return normalized || "maarif-soru";
};

const shortenText = (value: string, maxLength: number) => {
  const normalized = String(value || "").replace(/\s+/g, " ").trim();
  if (!normalized) return "";
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, Math.max(0, maxLength - 3)).trimEnd()}...`;
};

const wrapCanvasText = (ctx: CanvasRenderingContext2D, text: string, maxWidth: number) => {
  const normalized = String(text || "").replace(/\r\n/g, "\n");
  const paragraphs = normalized.split("\n");
  const lines: string[] = [];

  paragraphs.forEach((paragraph, paragraphIndex) => {
    const trimmedParagraph = paragraph.trim();
    if (!trimmedParagraph) {
      lines.push("");
      return;
    }

    const words = trimmedParagraph.split(/\s+/);
    let currentLine = "";

    words.forEach((word) => {
      const nextLine = currentLine ? `${currentLine} ${word}` : word;
      if (ctx.measureText(nextLine).width <= maxWidth || !currentLine) {
        currentLine = nextLine;
      } else {
        lines.push(currentLine);
        currentLine = word;
      }
    });

    if (currentLine) {
      lines.push(currentLine);
    }

    if (paragraphIndex < paragraphs.length - 1) {
      lines.push("");
    }
  });

  return lines;
};

const getWrappedTextHeight = (
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  lineHeight: number
) => {
  const lines = wrapCanvasText(ctx, text, maxWidth);
  const height = lines.reduce(
    (total, line) => total + (line ? lineHeight : Math.max(16, Math.floor(lineHeight * 0.6))),
    0
  );
  return { lines, height };
};

const loadImageElement = (src: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    if (!src.startsWith("data:")) {
      image.crossOrigin = "anonymous";
    }
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Soru görseli yüklenemedi."));
    image.src = src;
  });

const base64ToUint8Array = (base64: string) => {
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
};

const concatenateUint8Arrays = (chunks: Uint8Array[]) => {
  const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const merged = new Uint8Array(totalLength);
  let offset = 0;

  chunks.forEach((chunk) => {
    merged.set(chunk, offset);
    offset += chunk.length;
  });

  return merged;
};

const buildPdfFromJpegPages = (pageDataUrls: string[]) => {
  const encoder = new TextEncoder();
  const chunks: Uint8Array[] = [];
  const offsets: number[] = [0];
  let currentOffset = 0;

  const writeText = (value: string) => {
    const bytes = encoder.encode(value);
    chunks.push(bytes);
    currentOffset += bytes.length;
  };

  const writeBytes = (bytes: Uint8Array) => {
    chunks.push(bytes);
    currentOffset += bytes.length;
  };

  const writeObject = (objectNumber: number, writer: () => void) => {
    offsets[objectNumber] = currentOffset;
    writeText(`${objectNumber} 0 obj\n`);
    writer();
    writeText(`\nendobj\n`);
  };

  const pageObjectNumbers = pageDataUrls.map((_, index) => 3 + index * 3);
  const contentObjectNumbers = pageDataUrls.map((_, index) => 4 + index * 3);
  const imageObjectNumbers = pageDataUrls.map((_, index) => 5 + index * 3);
  const totalObjects = 2 + pageDataUrls.length * 3;

  writeText("%PDF-1.4\n");

  writeObject(1, () => {
    writeText("<< /Type /Catalog /Pages 2 0 R >>");
  });

  writeObject(2, () => {
    writeText(`<< /Type /Pages /Count ${pageDataUrls.length} /Kids [${pageObjectNumbers.map((value) => `${value} 0 R`).join(" ")}] >>`);
  });

  pageDataUrls.forEach((pageDataUrl, index) => {
    const imageObjectNumber = imageObjectNumbers[index];
    const pageObjectNumber = pageObjectNumbers[index];
    const contentObjectNumber = contentObjectNumbers[index];
    const imageBase64 = pageDataUrl.split(",")[1] || "";
    const imageBytes = base64ToUint8Array(imageBase64);
    const imageName = `Im${index + 1}`;
    const contentStream = `q\n${PDF_PAGE_WIDTH_PT} 0 0 ${PDF_PAGE_HEIGHT_PT} 0 0 cm\n/${imageName} Do\nQ`;
    const contentBytes = encoder.encode(contentStream);

    writeObject(pageObjectNumber, () => {
      writeText(
        `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PDF_PAGE_WIDTH_PT} ${PDF_PAGE_HEIGHT_PT}] /Resources << /XObject << /${imageName} ${imageObjectNumber} 0 R >> >> /Contents ${contentObjectNumber} 0 R >>`
      );
    });

    writeObject(contentObjectNumber, () => {
      writeText(`<< /Length ${contentBytes.length} >>\nstream\n`);
      writeBytes(contentBytes);
      writeText("\nendstream");
    });

    writeObject(imageObjectNumber, () => {
      writeText(
        `<< /Type /XObject /Subtype /Image /Width ${PDF_CANVAS_WIDTH} /Height ${PDF_CANVAS_HEIGHT} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${imageBytes.length} >>\nstream\n`
      );
      writeBytes(imageBytes);
      writeText("\nendstream");
    });
  });

  const xrefOffset = currentOffset;
  writeText(`xref\n0 ${totalObjects + 1}\n`);
  writeText("0000000000 65535 f \n");
  for (let index = 1; index <= totalObjects; index += 1) {
    writeText(`${String(offsets[index] || 0).padStart(10, "0")} 00000 n \n`);
  }
  writeText(`trailer\n<< /Size ${totalObjects + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`);

  return new Blob([concatenateUint8Arrays(chunks)], { type: "application/pdf" });
};

const renderQuestionPdfPages = async ({
  title,
  metadataItems,
  workflowSourceLabel,
  question,
}: {
  title: string;
  metadataItems?: QuestionPdfMetadataItem[];
  workflowSourceLabel: string;
  question: MaarifSingleQuestion;
}) => {
  const pages: string[] = [];
  let pageNumber = 0;
  let hasActivePage = false;
  let canvas!: HTMLCanvasElement;
  let ctx!: CanvasRenderingContext2D;
  let cursorY = 0;

  const startPage = () => {
    if (hasActivePage) {
      pages.push(canvas.toDataURL("image/jpeg", 0.92));
    }

    canvas = document.createElement("canvas");
    canvas.width = PDF_CANVAS_WIDTH;
    canvas.height = PDF_CANVAS_HEIGHT;
    const nextContext = canvas.getContext("2d");

    if (!nextContext) {
      throw new Error("PDF çıktısı hazırlanamadı.");
    }
    ctx = nextContext;
    hasActivePage = true;

    pageNumber += 1;
    cursorY = PDF_TOP_MARGIN_Y;

    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, PDF_CANVAS_WIDTH, PDF_CANVAS_HEIGHT);

    const compactTitle = shortenText(title, 68);
    const pageTitle = pageNumber > 1 ? `${compactTitle} (devam)` : compactTitle;
    ctx.fillStyle = "#0f172a";
    ctx.font = "bold 28px Arial";
    const rawTitleLines = wrapCanvasText(ctx, pageTitle, PDF_CONTENT_WIDTH);
    const titleLines = rawTitleLines.slice(0, 2);
    if (rawTitleLines.length > 2 && titleLines.length) {
      titleLines[titleLines.length - 1] = shortenText(titleLines[titleLines.length - 1], Math.max(12, titleLines[titleLines.length - 1].length - 3));
    }

    titleLines.forEach((line) => {
      ctx.fillText(line, PDF_MARGIN_X, cursorY);
      cursorY += 34;
    });

    ctx.strokeStyle = "#cbd5e1";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(PDF_MARGIN_X, cursorY + 4);
    ctx.lineTo(PDF_CANVAS_WIDTH - PDF_MARGIN_X, cursorY + 4);
    ctx.stroke();

    ctx.fillStyle = "#475569";
    ctx.font = "17px Arial";
    ctx.fillText(`Maarif Soru Sihirbazı PDF • Sayfa ${pageNumber}`, PDF_MARGIN_X, cursorY + 40);
    cursorY += 72;
  };

  const ensureSpace = (requiredHeight: number) => {
    if (cursorY + requiredHeight <= PDF_CANVAS_HEIGHT - PDF_BOTTOM_MARGIN_Y) return;
    startPage();
  };

  const measureParagraphHeight = (value: string, options?: { font?: string; lineHeight?: number }) => {
    const font = options?.font || "18px Arial";
    const lineHeight = options?.lineHeight || 28;
    const normalizedValue = normalizeRichTextForPdf(value);
    const { height } = buildPdfLineLayouts(ctx, normalizedValue, PDF_CONTENT_WIDTH, {
      font,
      lineHeight,
    });
    return height;
  };

  const measureParagraphHeightForWidth = (
    value: string,
    maxWidth: number,
    options?: { font?: string; lineHeight?: number }
  ) => {
    const font = options?.font || "18px Arial";
    const lineHeight = options?.lineHeight || 28;
    const normalizedValue = normalizeRichTextForPdf(value);
    const { height } = buildPdfLineLayouts(ctx, normalizedValue, maxWidth, {
      font,
      lineHeight,
    });
    return height;
  };

  const ensureSectionFits = (
    items: Array<{ value: string; font?: string; lineHeight?: number }>,
    options?: { titleGap?: number }
  ) => {
    const sectionTitleHeight = 56 + (options?.titleGap ?? 18);
    const totalHeight =
      sectionTitleHeight +
      items.reduce((sum, item) => sum + measureParagraphHeight(item.value, { font: item.font, lineHeight: item.lineHeight }), 0);
    const availablePageHeight = PDF_CANVAS_HEIGHT - PDF_TOP_MARGIN_Y - PDF_BOTTOM_MARGIN_Y;

    if (totalHeight <= availablePageHeight && cursorY + totalHeight > PDF_CANVAS_HEIGHT - PDF_BOTTOM_MARGIN_Y) {
      startPage();
    }
  };

  const drawSectionTitle = (value: string) => {
    const topGap = cursorY > PDF_TOP_MARGIN_Y + 8 ? 18 : 0;
    ensureSpace(48 + topGap);
    cursorY += topGap;
    ctx.fillStyle = "#0f766e";
    ctx.font = "bold 24px Arial";
    ctx.fillText(value, PDF_MARGIN_X, cursorY);
    cursorY += 38;
  };

  const drawWrappedParagraph = async (value: string, options?: { font?: string; color?: string; lineHeight?: number }) => {
    const font = options?.font || "18px Arial";
    const color = options?.color || "#1f2937";
    const lineHeight = options?.lineHeight || 28;
    const normalizedValue = normalizeRichTextForPdf(value);
    const fontSizeMatch = font.match(/(\d+(?:\.\d+)?)px/i);
    const baseFontSize = fontSizeMatch ? Number(fontSizeMatch[1]) : 18;

    const { lines, height } = buildPdfLineLayouts(ctx, normalizedValue, PDF_CONTENT_WIDTH, {
      font,
      lineHeight,
    });

    if (height > 0 && height <= PDF_CANVAS_HEIGHT - PDF_TOP_MARGIN_Y - PDF_BOTTOM_MARGIN_Y) {
      ensureSpace(height);
    }

    const fontWeight = font.toLowerCase().includes("bold") ? "bold" : "normal";
    const textFont = buildCanvasFont(baseFontSize, fontWeight, PDF_TEXT_FONT_FAMILY);
    ctx.fillStyle = color;
    ctx.textBaseline = "alphabetic";

    lines.forEach((line) => {
      ensureSpace(line.height);
      if (line.blank) {
        cursorY += line.height;
        return;
      }

      ctx.font = textFont;
      const spaceWidth = ctx.measureText(" ").width;
      let currentX = PDF_MARGIN_X;
      const baselineY = cursorY + line.ascent;

      line.tokens.forEach((token, index) => {
        if (index > 0) {
          currentX += spaceWidth;
        }

        drawPdfFormulaToken(ctx, token, currentX, baselineY, {
          font,
          color,
        });
        currentX += token.width;
      });

      cursorY += line.height;
    });
  };

  const drawWrappedParagraphAt = (
    x: number,
    y: number,
    maxWidth: number,
    value: string,
    options?: { font?: string; color?: string; lineHeight?: number }
  ) => {
    const font = options?.font || "18px Arial";
    const color = options?.color || "#1f2937";
    const lineHeight = options?.lineHeight || 28;
    const normalizedValue = normalizeRichTextForPdf(value);
    const fontSizeMatch = font.match(/(\d+(?:\.\d+)?)px/i);
    const baseFontSize = fontSizeMatch ? Number(fontSizeMatch[1]) : 18;
    const fontWeight = font.toLowerCase().includes("bold") ? "bold" : "normal";
    const textFont = buildCanvasFont(baseFontSize, fontWeight, PDF_TEXT_FONT_FAMILY);
    const { lines, height } = buildPdfLineLayouts(ctx, normalizedValue, maxWidth, {
      font,
      lineHeight,
    });

    ctx.fillStyle = color;
    ctx.textBaseline = "alphabetic";
    let currentY = y;

    lines.forEach((line) => {
      if (line.blank) {
        currentY += line.height;
        return;
      }

      ctx.font = textFont;
      const spaceWidth = ctx.measureText(" ").width;
      let currentX = x;
      const baselineY = currentY + line.ascent;

      line.tokens.forEach((token, index) => {
        if (index > 0) {
          currentX += spaceWidth;
        }

        drawPdfFormulaToken(ctx, token, currentX, baselineY, {
          font,
          color,
        });
        currentX += token.width;
      });

      currentY += line.height;
    });

    return height;
  };

  const drawLabelValue = async (label: string, value: string) => {
    await drawWrappedParagraph(`${label}: ${value || "-"}`, {
      font: "18px Arial",
      color: "#334155",
      lineHeight: 27,
    });
  };

  const measurePlainTextHeightForWidth = (value: string, maxWidth: number, options?: { font?: string; lineHeight?: number }) => {
    const normalizedValue = String(value || "").trim();
    if (!normalizedValue) return 0;

    ctx.font = options?.font || "16px Arial";
    return wrapCanvasText(ctx, normalizedValue, maxWidth).length * (options?.lineHeight || 22);
  };

  const drawPlainTextAt = (
    x: number,
    y: number,
    maxWidth: number,
    value: string,
    options?: { font?: string; color?: string; lineHeight?: number; align?: "left" | "center" }
  ) => {
    const normalizedValue = String(value || "").trim();
    if (!normalizedValue) return 0;

    const font = options?.font || "16px Arial";
    const color = options?.color || "#334155";
    const lineHeight = options?.lineHeight || 22;
    const align = options?.align || "left";
    ctx.font = font;
    ctx.fillStyle = color;
    ctx.textBaseline = "alphabetic";

    const lines = wrapCanvasText(ctx, normalizedValue, maxWidth);
    let currentY = y;
    lines.forEach((line) => {
      const textWidth = ctx.measureText(line).width;
      const textX = align === "center" ? x + Math.max(0, (maxWidth - textWidth) / 2) : x;
      ctx.fillText(line, textX, currentY + lineHeight * 0.78);
      currentY += lineHeight;
    });

    return lines.length * lineHeight;
  };

  startPage();

  const visibleMetadataItems = Array.isArray(metadataItems)
    ? metadataItems.filter((item) => String(item?.value || "").trim())
    : [];

  if (visibleMetadataItems.length || workflowSourceLabel) {
    ensureSectionFits([
      ...visibleMetadataItems.map((item) => ({ value: `${item.label}: ${item.value || "-"}`, font: "18px Arial", lineHeight: 27 })),
      ...(workflowSourceLabel ? [{ value: `Kaynak: ${workflowSourceLabel}`, font: "18px Arial", lineHeight: 27 }] : []),
      { value: "", font: "18px Arial", lineHeight: 28 },
    ]);
    drawSectionTitle("Soru Bilgileri");
    for (const item of visibleMetadataItems) {
      await drawLabelValue(item.label, item.value);
    }
    if (workflowSourceLabel) {
      await drawLabelValue("Kaynak", workflowSourceLabel);
    }
    await drawWrappedParagraph("");
  }

  const questionImageAssets = getQuestionImageAssets(question);
  if (questionImageAssets.length) {
    drawSectionTitle(questionImageAssets.length > 1 ? "Destek Görselleri" : "Destek Görseli");

    const prepareImageCard = async (
      imageAsset: MaarifQuestionImageAsset,
      cardWidth: number,
      maxImageHeight: number
    ) => {
      const image = await loadImageElement(getImageAssetSrc(imageAsset));
      const contentWidth = cardWidth - 24;
      const imageScale = Math.min(contentWidth / image.width, maxImageHeight / image.height, 1);
      const drawWidth = image.width * imageScale;
      const drawHeight = image.height * imageScale;
      const titleText = String(imageAsset.title || "").trim();
      const captionText =
        imageAsset.caption && imageAsset.caption.trim() !== titleText
          ? imageAsset.caption.trim()
          : "";
      const titleHeight = measurePlainTextHeightForWidth(titleText, contentWidth, {
        font: "bold 18px Arial",
        lineHeight: 24,
      });
      const captionHeight = measurePlainTextHeightForWidth(captionText, contentWidth, {
        font: "bold 16px Arial",
        lineHeight: 22,
      });
      const cardHeight =
        titleHeight +
        (titleHeight ? 8 : 0) +
        drawHeight +
        (captionHeight ? 12 + captionHeight : 0) +
        20;

      return {
        image,
        imageAsset,
        cardWidth,
        contentWidth,
        drawWidth,
        drawHeight,
        titleText,
        captionText,
        titleHeight,
        captionHeight,
        cardHeight,
      };
    };

    const drawImageCard = (
      card: Awaited<ReturnType<typeof prepareImageCard>>,
      x: number,
      y: number
    ) => {
      const contentX = x + 12;
      let localY = y + 8;

      if (card.titleText) {
        drawPlainTextAt(contentX, localY, card.contentWidth, card.titleText, {
          font: "bold 18px Arial",
          color: "#111827",
          lineHeight: 24,
          align: "center",
        });
        localY += card.titleHeight + 8;
      }

      const imageX = x + (card.cardWidth - card.drawWidth) / 2;
      ctx.drawImage(card.image, imageX, localY, card.drawWidth, card.drawHeight);
      localY += card.drawHeight;

      if (card.captionText) {
        localY += 12;
        drawPlainTextAt(contentX, localY, card.contentWidth, card.captionText, {
          font: "bold 16px Arial",
          color: "#111827",
          lineHeight: 22,
          align: "center",
        });
      }
    };

    if (questionImageAssets.length === 1) {
      const imageAsset = questionImageAssets[0];
      try {
        const card = await prepareImageCard(imageAsset, PDF_CONTENT_WIDTH, 420);
        ensureSpace(card.cardHeight + 16);
        drawImageCard(card, PDF_MARGIN_X, cursorY);
        cursorY += card.cardHeight + 20;
      } catch (error) {
        await drawLabelValue("Destek Görseli", "Yüklenemedi");
      }
    } else {
      const gap = 36;
      const twoColumnWidth = (PDF_CONTENT_WIDTH - gap) / 2;

      for (let index = 0; index < questionImageAssets.length; index += 2) {
        const rowAssets = questionImageAssets.slice(index, index + 2);
        const isCenteredLastRow = rowAssets.length === 1;
        const cardWidth = isCenteredLastRow ? Math.min(620, PDF_CONTENT_WIDTH * 0.66) : twoColumnWidth;
        const maxImageHeight = isCenteredLastRow ? 320 : 270;
        const cards = [];

        for (let itemIndex = 0; itemIndex < rowAssets.length; itemIndex += 1) {
          try {
            cards.push(await prepareImageCard(rowAssets[itemIndex], cardWidth, maxImageHeight));
          } catch (error) {
            await drawLabelValue(`Destek Görseli ${index + itemIndex + 1}`, "Yüklenemedi");
          }
        }

        if (!cards.length) continue;

        const rowHeight = Math.max(...cards.map((card) => card.cardHeight));
        ensureSpace(rowHeight + 28);
        cards.forEach((card, cardIndex) => {
          const x = isCenteredLastRow
            ? PDF_MARGIN_X + (PDF_CONTENT_WIDTH - card.cardWidth) / 2
            : PDF_MARGIN_X + cardIndex * (twoColumnWidth + gap);
          drawImageCard(card, x, cursorY);
        });
        cursorY += rowHeight + 28;
      }
    }
  }

  ensureSectionFits([
    { value: question.context, font: "18px Arial", lineHeight: 28 },
    { value: "", font: "18px Arial", lineHeight: 28 },
  ]);
  drawSectionTitle("Bağlam");
  await drawWrappedParagraph(question.context, { font: "18px Arial", color: "#111827", lineHeight: 28 });
  await drawWrappedParagraph("");

  ensureSectionFits([
    { value: question.questionStem, font: "bold 20px Arial", lineHeight: 31 },
    { value: "", font: "18px Arial", lineHeight: 28 },
  ]);
  drawSectionTitle("Soru Kökü");
  await drawWrappedParagraph(question.questionStem, { font: "bold 20px Arial", color: "#111827", lineHeight: 31 });
  await drawWrappedParagraph("");

  const choiceLayout = getQuestionChoiceLayout(question);
  ensureSectionFits([
    ...choiceKeys.map((key) => ({
      value: `${key}) ${question.choices[key]}`,
      font: key === question.correctAnswer ? "bold 18px Arial" : "18px Arial",
      lineHeight: 28,
    })),
    { value: "", font: "18px Arial", lineHeight: 28 },
  ]);
  drawSectionTitle("Seçenekler");
  if (choiceLayout === "stacked") {
    for (const key of choiceKeys) {
      const choiceLabel = `${key}) ${question.choices[key]}`;
      const choiceImageAssets = getChoiceImageAssets(question, key);
      const textFont = key === question.correctAnswer ? "bold 18px Arial" : "18px Arial";
      const textColor = key === question.correctAnswer ? "#065f46" : "#1f2937";
      const textHeight = measureParagraphHeightForWidth(choiceLabel, PDF_CONTENT_WIDTH, {
        font: textFont,
        lineHeight: 28,
      });

      const loadedChoiceImages: Array<{ image: HTMLImageElement; drawWidth: number; drawHeight: number }> = [];
      for (const choiceImageAsset of choiceImageAssets) {
        const choiceImageSrc = getImageAssetSrc(choiceImageAsset);
        if (!choiceImageSrc) continue;

        try {
          const choiceImage = await loadImageElement(choiceImageSrc);
          const maxChoiceImageWidth = Math.min(PDF_CONTENT_WIDTH - 40, 420);
          const maxChoiceImageHeight = 220;
          const choiceImageScale = Math.min(
            maxChoiceImageWidth / choiceImage.width,
            maxChoiceImageHeight / choiceImage.height,
            1
          );
          loadedChoiceImages.push({
            image: choiceImage,
            drawWidth: choiceImage.width * choiceImageScale,
            drawHeight: choiceImage.height * choiceImageScale,
          });
        } catch (error) {
          // Görsel yüklenemezse şık metnini basmaya devam ediyoruz.
        }
      }

      const totalBlockHeight =
        textHeight +
        loadedChoiceImages.reduce((sum, item) => sum + item.drawHeight + 26, 0) +
        8;
      ensureSpace(totalBlockHeight);
      await drawWrappedParagraph(choiceLabel, {
        font: textFont,
        color: textColor,
        lineHeight: 28,
      });

      for (const choiceImage of loadedChoiceImages) {
        const imageX = PDF_MARGIN_X + 28;
        ctx.fillStyle = "#ffffff";
        ctx.strokeStyle = "#d1d5db";
        ctx.lineWidth = 2;
        ctx.fillRect(imageX - 10, cursorY - 8, choiceImage.drawWidth + 20, choiceImage.drawHeight + 20);
        ctx.strokeRect(imageX - 10, cursorY - 8, choiceImage.drawWidth + 20, choiceImage.drawHeight + 20);
        ctx.drawImage(choiceImage.image, imageX, cursorY, choiceImage.drawWidth, choiceImage.drawHeight);
        cursorY += choiceImage.drawHeight + 22;
      }
    }
  } else {
    const columns = choiceLayout === "five-column" ? 5 : 2;
    const gap = choiceLayout === "five-column" ? 14 : 22;
    const columnWidth = (PDF_CONTENT_WIDTH - gap * (columns - 1)) / columns;

    for (let index = 0; index < choiceKeys.length; index += columns) {
      const rowKeys = choiceKeys.slice(index, index + columns);
      const rowHeight = Math.max(
        ...rowKeys.map((key) =>
          measureParagraphHeightForWidth(`${key}) ${question.choices[key]}`, columnWidth, {
            font: key === question.correctAnswer ? "bold 18px Arial" : "18px Arial",
            lineHeight: 28,
          })
        ),
        0
      );

      ensureSpace(rowHeight + 10);
      rowKeys.forEach((key, columnIndex) => {
        drawWrappedParagraphAt(
          PDF_MARGIN_X + columnIndex * (columnWidth + gap),
          cursorY,
          columnWidth,
          `${key}) ${question.choices[key]}`,
          {
            font: key === question.correctAnswer ? "bold 18px Arial" : "18px Arial",
            color: key === question.correctAnswer ? "#065f46" : "#1f2937",
            lineHeight: 28,
          }
        );
      });
      cursorY += rowHeight + 10;
    }
  }
  await drawWrappedParagraph("");

  ensureSectionFits([
    { value: `Doğru Cevap: ${question.correctAnswer || "-"}`, font: "18px Arial", lineHeight: 27 },
    { value: question.shortJustification, font: "18px Arial", lineHeight: 28 },
    { value: "", font: "18px Arial", lineHeight: 28 },
  ]);
  drawSectionTitle("Doğru Cevap ve Gerekçe");
  await drawLabelValue("Doğru Cevap", question.correctAnswer || "-");
  await drawWrappedParagraph(question.shortJustification, { font: "18px Arial", color: "#1f2937", lineHeight: 28 });
  await drawWrappedParagraph("");

  ensureSectionFits(
    choiceKeys.map((key) => ({
      value: `${key}) ${question.distractorAnalysis[key]}`,
      font: "18px Arial",
      lineHeight: 28,
    }))
  );
  drawSectionTitle("Çeldirici Analizi");
  for (const key of choiceKeys) {
    await drawWrappedParagraph(`${key}) ${question.distractorAnalysis[key]}`, {
      font: "18px Arial",
      color: "#334155",
      lineHeight: 28,
    });
  }

  if (hasActivePage) {
    pages.push(canvas.toDataURL("image/jpeg", 0.92));
  }

  return pages;
};

const renderQuestionCollectionPdfPages = async ({
  title,
  metadataItems,
  getMetadataItems,
  workflowSourceLabel,
  questions,
}: {
  title: string;
  metadataItems?: QuestionPdfMetadataItem[];
  getMetadataItems?: (question: MaarifSingleQuestion, index: number) => QuestionPdfMetadataItem[];
  workflowSourceLabel: string;
  questions: MaarifSingleQuestion[];
}) => {
  const pages: string[] = [];

  for (let index = 0; index < questions.length; index += 1) {
    const question = questions[index];
    const questionPages = await renderQuestionPdfPages({
      title: `${title} • Soru ${index + 1}`,
      metadataItems: getMetadataItems ? getMetadataItems(question, index) : metadataItems,
      workflowSourceLabel,
      question,
    });
    pages.push(...questionPages);
  }

  return pages;
};

function QuestionPreviewCard({
  title,
  question,
  tone = "emerald",
}: {
  title: string;
  question: MaarifSingleQuestion | null;
  tone?: "emerald" | "amber";
}) {
  if (!question) return null;

  const toneClass =
    tone === "amber"
      ? "border-amber-200 bg-amber-50/50"
      : "border-emerald-200 bg-emerald-50/50";
  const choiceLayout = getQuestionChoiceLayout(question);
  const choiceGridClass =
    choiceLayout === "five-column"
      ? "grid grid-cols-2 md:grid-cols-5 gap-3"
      : choiceLayout === "two-column"
        ? "grid grid-cols-1 md:grid-cols-2 gap-3"
        : "grid grid-cols-1 gap-3";
  const questionImageAssets = getQuestionImageAssets(question);
  const validationWarnings = getQuestionValidationWarnings(question);
  const needsReview = questionNeedsReview(question);

  return (
    <div className={`rounded-2xl border ${toneClass} p-5`}>
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <div className="text-sm font-semibold text-gray-900">{title}</div>
          <div className="text-xs text-gray-500">
            {question.measuredProcessComponentCode} • {question.measuredProcessComponentLabel}
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {needsReview ? (
            <span className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-900">
              <AlertTriangle size={14} />
              Gözden Geçir
            </span>
          ) : null}
          <span className="rounded-full border border-white/70 bg-white px-3 py-1 text-xs font-medium text-gray-700">
            5 Şık
          </span>
        </div>
      </div>

      <div className="space-y-4">
        {validationWarnings.length ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
            <div className="font-semibold">Doğrulama uyarısı</div>
            <div className="mt-1 leading-6">{validationWarnings[0].reason}</div>
          </div>
        ) : null}

        {questionImageAssets.length ? (
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
              {questionImageAssets.length > 1 ? "Destek Görselleri" : "Destek Görseli"}
            </div>
            <div
              className={`grid grid-cols-1 gap-3 rounded-xl bg-white px-4 py-4 border border-gray-200 ${
                questionImageAssets.length > 1 ? "md:grid-cols-2" : "md:grid-cols-1"
              }`}
            >
              {questionImageAssets.map((asset, index) => {
                const isCenteredLast =
                  questionImageAssets.length > 1 &&
                  questionImageAssets.length % 2 === 1 &&
                  index === questionImageAssets.length - 1;

                return (
                <div
                  key={`${getImageAssetKey(asset)}-${index}`}
                  className={`space-y-2 ${isCenteredLast ? "md:col-span-2 md:mx-auto md:w-2/3" : ""}`}
                >
                  {asset.title || asset.caption ? (
                    <div className="text-xs font-semibold text-gray-600">{asset.title || asset.caption}</div>
                  ) : null}
                  <img
                    src={getImageAssetSrc(asset)}
                    alt={asset.title || asset.caption || `Soru destek görseli ${index + 1}`}
                    className="w-full max-h-[340px] object-contain rounded-xl bg-white"
                  />
                  {asset.caption && asset.caption !== asset.title ? (
                    <div className="text-xs font-semibold leading-5 text-gray-800">{asset.caption}</div>
                  ) : null}
                </div>
                );
              })}
            </div>
          </div>
        ) : null}

        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Bağlam</div>
          <div className="rounded-xl bg-white px-4 py-3 text-sm text-gray-700 border border-gray-200">
            <RichTextContent value={question.context} className="text-base leading-8 text-gray-700" />
          </div>
        </div>

        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Soru Kökü</div>
          <div className="rounded-xl bg-white px-4 py-4 text-gray-900 border border-gray-200">
            <RichTextContent value={question.questionStem} className="text-lg leading-8 font-medium text-gray-900" />
          </div>
        </div>

        <div className={choiceGridClass}>
          {choiceKeys.map((key) => (
            <div
              key={key}
              className={`rounded-xl border px-4 py-3 text-sm ${
                question.correctAnswer === key
                  ? "border-emerald-300 bg-emerald-100/80 text-emerald-900"
                  : "border-gray-200 bg-white text-gray-700"
              }`}
            >
              <div>
                <span className="font-semibold mr-2">{key})</span>
                <RichTextContent value={question.choices[key]} className="inline text-lg leading-7" inline />
              </div>
              {getChoiceImageAssets(question, key).length ? (
                <div className="mt-3 rounded-xl border border-gray-200 bg-white p-3">
                  <div className="space-y-3">
                    {getChoiceImageAssets(question, key).map((asset, index) => (
                      <img
                        key={`${getImageAssetKey(asset)}-${index}`}
                        src={getImageAssetSrc(asset)}
                        alt={`${key} şıkkı görseli ${index + 1}`}
                        className="w-full max-h-[220px] object-contain rounded-lg bg-white"
                      />
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <div className="rounded-xl bg-white border border-gray-200 p-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Kısa Gerekçe</div>
            <RichTextContent value={question.shortJustification} className="text-sm text-gray-700" />
          </div>
          <div className="rounded-xl bg-white border border-gray-200 p-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Çeldirici Analizi</div>
            <div className="space-y-2 text-sm text-gray-700">
              {choiceKeys.map((key) => (
                <div key={key}>
                  <span className="font-semibold mr-2">{key})</span>
                  <RichTextContent value={question.distractorAnalysis[key]} className="inline text-sm text-gray-700" inline />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function QuestionCollectionPreview({
  title,
  questions,
}: {
  title: string;
  questions: MaarifSingleQuestion[];
}) {
  if (!questions.length) return null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-lg font-semibold text-gray-900">{title}</div>
          <div className="text-sm text-gray-500">{questions.length} soru üretildi</div>
        </div>
        <span className="rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-semibold text-sky-800">
          Çoklu Soru
        </span>
      </div>

      <div className="space-y-4">
        {questions.map((item, index) => (
          <QuestionPreviewCard key={`${item.questionStem}-${index}`} title={`Soru ${index + 1}`} question={item} />
        ))}
      </div>
    </div>
  );
}

export default function MaarifQuestionWizardWorkspacePage() {
  const navigate = useNavigate();
  const params = useParams();
  const { showAlert, showConfirm, showToast } = useModal();
  const recordId = params.id;
  const isEdit = Boolean(recordId);
  const canList = checkPermission("maarif_question_list");
  const canCreate = checkPermission("maarif_question_add");
  const canUpdate = checkPermission("maarif_question_update");
  const canDelete = checkPermission("maarif_question_delete");
  const canConvertPdfSurface = canCreate && checkPermission(MAARIF_QUESTION_PDF_SURFACE_CONVERT_PERMISSION);
  const canConvertPdfContextual = canCreate && checkPermission(MAARIF_QUESTION_PDF_CONTEXTUAL_CONVERT_PERMISSION);
  const canExportToQuestionBank = checkPermission("question_bank_add") && checkPermission("question_bank_list");

  const [lessons, setLessons] = useState<LessonOption[]>([]);
  const [loadingLessons, setLoadingLessons] = useState(true);
  const [loadingRecord, setLoadingRecord] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exportingQuestionBank, setExportingQuestionBank] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [contextLoading, setContextLoading] = useState(false);
  const [questionLoading, setQuestionLoading] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);
  const [rewriteLoading, setRewriteLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [workflowSource, setWorkflowSource] = useState<MaarifWorkflowSource>("generated");
  const [questionMode, setQuestionMode] = useState<QuestionMode>("context");
  const [panelTab, setPanelTab] = useState<WorkspacePanelTab>("workflow");
  const isSuperAdmin = checkSuperAdmin();
  const [adminPassword, setAdminPassword] = useState("");
  const [adminUnlocked, setAdminUnlocked] = useState(false);
  const [adminPasswordError, setAdminPasswordError] = useState(false);
  const [adminPromptConfigs, setAdminPromptConfigs] = useState<MaarifPromptConfig[]>([]);
  const [adminPromptsLoading, setAdminPromptsLoading] = useState(false);
  const [adminEditingKey, setAdminEditingKey] = useState<string | null>(null);
  const [adminEditingContent, setAdminEditingContent] = useState("");
  const [adminSaving, setAdminSaving] = useState(false);
  const [auditMode, setAuditMode] = useState<"generated" | "external">("generated");
  const [form, setForm] = useState({
    title: "",
    lessonId: "",
    gradeLabel: "7. Sınıf",
    difficulty: "Orta" as MaarifQuestionDifficulty,
    lengthPreset: "Orta" as MaarifQuestionLengthPreset,
    targetWordCount: "",
    wantsVisual: false,
    customPrompt: "",
    gainText: "",
    processComponentCode: MAARIF_PROCESS_COMPONENTS[0].code,
  });
  const [contextText, setContextText] = useState("");
  const [contextRationale, setContextRationale] = useState("");
  const [question, setQuestion] = useState<MaarifSingleQuestion | null>(null);
  const [multiQuestions, setMultiQuestions] = useState<MaarifSingleQuestion[]>([]);
  const [detectedMetadata, setDetectedMetadata] = useState<MaarifQuestionDetectedMetadata | null>(null);
  const [questionSourceAnalysis, setQuestionSourceAnalysis] = useState<MaarifQuestionSourceAnalysis | null>(null);
  const [questionImageFile, setQuestionImageFile] = useState<File | null>(null);
  const [questionImagePreview, setQuestionImagePreview] = useState("");
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [pdfFileName, setPdfFileName] = useState("");
  const [pdfPageCount, setPdfPageCount] = useState(0);
  const [selectedPdfPages, setSelectedPdfPages] = useState<number[]>([]);
  const [pdfPageModalOpen, setPdfPageModalOpen] = useState(false);
  const [pdfMetadataLoading, setPdfMetadataLoading] = useState(false);
  const [pdfSummary, setPdfSummary] = useState<{ questionCount: number; notes?: string } | null>(null);
  const [pdfJob, setPdfJob] = useState<MaarifQuestionPdfJobResponse | null>(null);
  const [externalQuestionText, setExternalQuestionText] = useState("");
  const [auditReport, setAuditReport] = useState<MaarifQuestionAuditReport | null>(null);
  const [auditImage, setAuditImage] = useState<File | null>(null);
  const [auditImagePreview, setAuditImagePreview] = useState("");
  const [rewriteQuestion, setRewriteQuestion] = useState<MaarifSingleQuestion | null>(null);
  const [questionBankExport, setQuestionBankExport] = useState<MaarifQuestionBankExportInfo | null>(null);
  const [modelInfo, setModelInfo] = useState<{
    contextModel?: string | null;
    questionModel?: string | null;
    questionFromImageModel?: string | null;
    questionsFromPdfModel?: string | null;
    questionImageModel?: string | null;
    auditModel?: string | null;
    rewriteModel?: string | null;
  }>({});
  const [recordMeta, setRecordMeta] = useState<Pick<MaarifQuestionRecord, "createdAt" | "updatedAt"> | null>(null);
  const routeRecordId = recordId ? String(recordId) : null;
  const sessionRestoredRef = useRef(false);
  const handledSessionTaskIdRef = useRef<string | null>(null);
  const latestSnapshotRef = useRef<MaarifWizardSnapshot | null>(null);
  const latestTransientRef = useRef<MaarifWizardTransientAssets | null>(null);
  const skipSessionPersistOnLeaveRef = useRef(false);
  const applyWizardSnapshotRef = useRef<((snapshot: MaarifWizardSnapshot, transient: MaarifWizardTransientAssets) => void) | null>(null);
  const applyPendingTaskLoadingStateRef = useRef<((task: MaarifWizardPendingTask | null) => void) | null>(null);
  const handleCompletedSessionTaskRef = useRef<((task: MaarifWizardPendingTask) => Promise<void>) | null>(null);
  const handleFailedSessionTaskRef = useRef<((task: MaarifWizardPendingTask) => Promise<void>) | null>(null);

  const selectedLessonTitle = useMemo(
    () => lessons.find((lesson) => lesson.id === form.lessonId)?.title || detectedMetadata?.lessonTitle || "",
    [detectedMetadata?.lessonTitle, lessons, form.lessonId]
  );

  const selectedProcessComponent = useMemo(
    () => MAARIF_PROCESS_COMPONENTS.find((item) => item.code === form.processComponentCode) || MAARIF_PROCESS_COMPONENTS[0],
    [form.processComponentCode]
  );
  const summaryLessonTitle =
    questionMode === "image" ? detectedMetadata?.lessonTitle || "" : selectedLessonTitle;
  const summaryGradeLabel =
    questionMode === "image" ? detectedMetadata?.gradeLabel || "" : form.gradeLabel || "";
  const summaryGainText =
    questionMode === "image" ? detectedMetadata?.gainText || "" : form.gainText || "";
  const summaryProcessComponentText =
    questionMode === "image"
      ? detectedMetadata?.processComponentCode && detectedMetadata?.processComponentLabel
        ? `${detectedMetadata.processComponentCode} - ${detectedMetadata.processComponentLabel}`
        : ""
      : form.processComponentCode
        ? `${form.processComponentCode} - ${selectedProcessComponent.label}`
        : "";
  const isPdfMode = isPdfQuestionMode(questionMode);
  const pdfRewriteMode = getPdfRewriteMode(questionMode);
  const isDifficultyLocked = isPdfMode;
  const pdfJobStatusLabel =
    pdfJob?.status === "queued"
      ? "Kuyrukta"
      : pdfJob?.status === "processing"
        ? "İşleniyor"
        : pdfJob?.status === "completed"
          ? "Tamamlandı"
      : pdfJob?.status === "failed"
        ? "Hata"
        : "Hazır";
  const pdfJobStageLabel = getPdfJobStageLabel(pdfJob);

  const canUseQuestionMode = useCallback(
    (mode: QuestionMode) => {
      if (mode === "pdf") return canConvertPdfSurface;
      if (mode === "pdf_contextual") return canConvertPdfContextual;
      return canCreate;
    },
    [canConvertPdfContextual, canConvertPdfSurface, canCreate]
  );

  const resetLoadingStates = useCallback(() => {
    setContextLoading(false);
    setQuestionLoading(false);
    setAuditLoading(false);
    setRewriteLoading(false);
  }, []);

  const applyPendingTaskLoadingState = useCallback(
    (task: MaarifWizardPendingTask | null) => {
      resetLoadingStates();
      if (!task || task.status !== "running") return;

      if (task.type === "context") {
        setContextLoading(true);
        return;
      }

      if (task.type === "audit") {
        setAuditLoading(true);
        return;
      }

      if (task.type === "rewrite") {
        setRewriteLoading(true);
        return;
      }

      setQuestionLoading(true);
    },
    [resetLoadingStates]
  );

  const buildWizardSnapshot = useCallback(
    (): MaarifWizardSnapshot => ({
      currentStep,
      workflowSource,
      questionMode,
      panelTab,
      auditMode,
      form,
      contextText,
      contextRationale,
      question,
      multiQuestions,
      detectedMetadata,
      questionSourceAnalysis,
      pdfFileName,
      pdfPageCount,
      selectedPdfPages,
      pdfSummary,
      pdfJob,
      externalQuestionText,
      auditReport,
      rewriteQuestion,
      questionBankExport,
      modelInfo,
      recordMeta,
    }),
    [
      auditMode,
      auditReport,
      contextRationale,
      contextText,
      currentStep,
      detectedMetadata,
      externalQuestionText,
      form,
      modelInfo,
      multiQuestions,
      panelTab,
      pdfFileName,
      pdfJob,
      pdfPageCount,
      pdfSummary,
      question,
      questionBankExport,
      questionMode,
      questionSourceAnalysis,
      recordMeta,
      rewriteQuestion,
      selectedPdfPages,
      workflowSource,
    ]
  );

  const buildWizardTransientAssets = useCallback(
    (): MaarifWizardTransientAssets => ({
      pdfFile,
      questionImageFile,
      questionImagePreview,
      auditImage,
      auditImagePreview,
    }),
    [auditImage, auditImagePreview, pdfFile, questionImageFile, questionImagePreview]
  );

  const hasMeaningfulWizardState = useCallback(
    (snapshot: MaarifWizardSnapshot, transient: MaarifWizardTransientAssets, session: MaarifWizardSessionState | null) => {
      if (isEdit && !session) return false;
      if (session?.pendingTask) return true;
      if (transient.pdfFile || transient.questionImageFile || transient.auditImage) return true;
      if (snapshot.pdfJob) return true;
      if (snapshot.question || snapshot.rewriteQuestion) return true;
      if (snapshot.multiQuestions.length || snapshot.auditReport || snapshot.questionSourceAnalysis) return true;
      if (snapshot.contextText.trim() || snapshot.contextRationale.trim()) return true;
      if (snapshot.externalQuestionText.trim()) return true;
      if (snapshot.pdfFileName || snapshot.selectedPdfPages.length || snapshot.pdfSummary) return true;
      if (snapshot.questionMode !== "context" || snapshot.currentStep !== 1 || snapshot.auditMode !== "generated") return true;
      if (!isEdit && (snapshot.form.title.trim() || snapshot.form.customPrompt.trim() || snapshot.form.gainText.trim())) return true;
      return false;
    },
    [isEdit]
  );

  const applyWizardSnapshot = useCallback((snapshot: MaarifWizardSnapshot, transient: MaarifWizardTransientAssets) => {
    setCurrentStep(snapshot.currentStep);
    setWorkflowSource(snapshot.workflowSource);
    setQuestionMode(snapshot.questionMode);
    setPanelTab(snapshot.panelTab);
    setAuditMode(snapshot.auditMode);
    setForm(snapshot.form);
    setContextText(snapshot.contextText);
    setContextRationale(snapshot.contextRationale);
    setQuestion(snapshot.question);
    setMultiQuestions(snapshot.multiQuestions || []);
    setDetectedMetadata(snapshot.detectedMetadata || null);
    setQuestionSourceAnalysis(snapshot.questionSourceAnalysis || null);
    setPdfFileName(snapshot.pdfFileName || transient.pdfFile?.name || "");
    setPdfFile(transient.pdfFile || null);
    setPdfPageCount(snapshot.pdfPageCount || 0);
    setSelectedPdfPages(snapshot.selectedPdfPages || []);
    setPdfSummary(snapshot.pdfSummary || null);
    setPdfJob(snapshot.pdfJob || null);
    setExternalQuestionText(snapshot.externalQuestionText || "");
    setAuditReport(snapshot.auditReport || null);
    setRewriteQuestion(snapshot.rewriteQuestion || null);
    setQuestionBankExport(snapshot.questionBankExport || null);
    setModelInfo(snapshot.modelInfo || {});
    setRecordMeta(snapshot.recordMeta || null);
    setQuestionImageFile(transient.questionImageFile || null);
    setQuestionImagePreview(
      transient.questionImagePreview || (transient.questionImageFile ? URL.createObjectURL(transient.questionImageFile) : "")
    );
    setAuditImage(transient.auditImage || null);
    setAuditImagePreview(
      transient.auditImagePreview || (transient.auditImage ? URL.createObjectURL(transient.auditImage) : "")
    );
  }, []);

  const applyCompletedPdfJob = useCallback(
    (jobResponse: MaarifQuestionPdfJobResponse) => {
      const result = jobResponse.result;
      if (!result) {
        throw new Error("PDF işi tamamlandı ancak sonuç alınamadı.");
      }

      const normalizedPdfQuestions = normalizeQuestionCollectionProcessComponents(
        result.questions || [],
        form.processComponentCode || MAARIF_PROCESS_COMPONENTS[0].code
      );

      setQuestion(null);
      setRewriteQuestion(null);
      setMultiQuestions(normalizedPdfQuestions);
      setPdfSummary(result.summary || { questionCount: normalizedPdfQuestions.length || 0 });
      setPdfFileName(result.sourceFileName || pdfFile?.name || jobResponse.sourceFile?.name || "");
      setSelectedPdfPages(result.selectedPages || selectedPdfPages);
      setDetectedMetadata(result.detectedMetadata || null);
      setQuestionSourceAnalysis(null);
      setAuditReport(null);
      setWorkflowSource("generated");
      setPdfJob(jobResponse);
      setModelInfo((prev) => ({
        ...prev,
        questionModel: null,
        questionFromImageModel: null,
        questionsFromPdfModel: result.model || null,
        questionImageModel: null,
        auditModel: null,
        rewriteModel: null,
      }));
      setCurrentStep(2);
      showToast(
        result.rewriteMode === "contextual"
          ? `${normalizedPdfQuestions.length} soru bağlamı değiştirilerek üretildi.`
          : `${normalizedPdfQuestions.length} soru PDF'ten çevrildi.`,
        "success"
      );
    },
    [form.processComponentCode, pdfFile?.name, selectedPdfPages, showToast]
  );

  const handleCompletedSessionTask = useCallback(
    async (task: MaarifWizardPendingTask) => {
      if (!task.result) {
        throw new Error("Arka plan işlemi tamamlandı ancak sonuç alınamadı.");
      }

      if (task.type === "context") {
        const result: any = task.result;
        setContextText(result.context || "");
        setContextRationale(result.rationale || "");
        setModelInfo((prev) => ({ ...prev, contextModel: result.model || null }));
        setCurrentStep(2);
        showToast("Bağlam oluşturuldu.", "success");
      } else if (task.type === "question") {
        const result: any = task.result;
        setQuestion(result.question);
        setMultiQuestions([]);
        setPdfSummary(null);
        setPdfJob(null);
        setPdfFileName("");
        setDetectedMetadata(null);
        setQuestionSourceAnalysis(null);
        setWorkflowSource("generated");
        setModelInfo((prev) => ({
          ...prev,
          questionModel: result.model || null,
          questionFromImageModel: null,
          questionsFromPdfModel: null,
          questionImageModel: result.imageModel || null,
        }));
        setCurrentStep(3);
        setAuditMode("generated");
        if (form.wantsVisual && !result.question?.image?.base64) {
          showToast(result.imageError || "Soru üretildi ancak destek görseli oluşturulamadı.", "warning");
        }
        showToast("Soru üretildi.", "success");
      } else if (task.type === "image") {
        const result: any = task.result;
        setQuestion(result.question);
        setMultiQuestions([]);
        setPdfSummary(null);
        setPdfJob(null);
        setPdfFileName("");
        setDetectedMetadata(result.detectedMetadata || null);
        setQuestionSourceAnalysis(result.analysis || null);
        if (result.detectedMetadata) {
          const normalizedLessonTitle = String(result.detectedMetadata.lessonTitle || "").trim().toLocaleLowerCase("tr-TR");
          const matchedLesson = normalizedLessonTitle
            ? lessons.find((lesson) => lesson.title.trim().toLocaleLowerCase("tr-TR") === normalizedLessonTitle)
            : null;

          setForm((prev) => ({
            ...prev,
            lessonId: matchedLesson?.id || prev.lessonId || "",
            gradeLabel: result.detectedMetadata?.gradeLabel || prev.gradeLabel,
            gainText: result.detectedMetadata?.gainText || prev.gainText,
            processComponentCode: result.detectedMetadata?.processComponentCode || prev.processComponentCode,
          }));
        }
        setWorkflowSource("generated");
        setModelInfo((prev) => ({
          ...prev,
          questionModel: null,
          questionFromImageModel: result.model || null,
          questionsFromPdfModel: null,
          questionImageModel: result.imageModel || null,
        }));
        setCurrentStep(2);
        setAuditMode("generated");
        if (form.wantsVisual && !result.question?.image?.base64) {
          showToast(result.imageError || "Soru üretildi ancak destek görseli oluşturulamadı.", "warning");
        }
        showToast("Soru görselden analiz edilip yeniden oluşturuldu.", "success");
      } else if (task.type === "pdf") {
        applyCompletedPdfJob(task.result as MaarifQuestionPdfJobResponse);
      } else if (task.type === "audit") {
        const result: any = task.result;
        setAuditReport(result.report);
        setModelInfo((prev) => ({ ...prev, auditModel: result.model || null }));
        if (auditMode === "external") {
          setWorkflowSource("audited_upload");
        }
        showToast("Denetim raporu hazır.", "success");
      } else if (task.type === "rewrite") {
        const result: any = task.result;
        setRewriteQuestion(result.question);
        setWorkflowSource("audited_upload");
        setModelInfo((prev) => ({ ...prev, rewriteModel: result.model || null }));
        showToast("Yeni uyumlu soru üretildi.", "success");
      }

      resetLoadingStates();
      clearMaarifWizardPendingTask(routeRecordId, task.id);
    },
    [applyCompletedPdfJob, auditMode, form.wantsVisual, lessons, resetLoadingStates, routeRecordId, showToast]
  );

  const handleFailedSessionTask = useCallback(
    async (task: MaarifWizardPendingTask) => {
      const titleMap: Record<MaarifWizardPendingTask["type"], string> = {
        context: "Bağlam üretilemedi",
        question: "Soru üretilemedi",
        image: "Görselden soru üretilemedi",
        pdf: "PDF işlenemedi",
        audit: "Denetim yapılamadı",
        rewrite: "Yeniden üretilemedi",
      };

      resetLoadingStates();
      await showAlert({
        title: titleMap[task.type],
        message: task.error || "İşlem sırasında bir hata oluştu.",
        type: "error",
      });
      clearMaarifWizardPendingTask(routeRecordId, task.id);
    },
    [resetLoadingStates, routeRecordId, showAlert]
  );

  const questionCount = multiQuestions.length || (rewriteQuestion || question ? 1 : 0);

  const generatedQuestionForAudit = useMemo(() => rewriteQuestion || question, [question, rewriteQuestion]);
  const generatedQuestionText = useMemo(() => formatQuestionForAudit(generatedQuestionForAudit), [generatedQuestionForAudit]);

  useEffect(() => {
    applyWizardSnapshotRef.current = applyWizardSnapshot;
    applyPendingTaskLoadingStateRef.current = applyPendingTaskLoadingState;
    handleCompletedSessionTaskRef.current = handleCompletedSessionTask;
    handleFailedSessionTaskRef.current = handleFailedSessionTask;
  }, [applyPendingTaskLoadingState, applyWizardSnapshot, handleCompletedSessionTask, handleFailedSessionTask]);

  useEffect(() => {
    const loadLessons = async () => {
      try {
        const response = await ApiService.getAllLessons();
        const rows = Array.isArray(response) ? response : response?.data || [];
        const mappedLessons = rows.map((lesson: any) => ({
          id: lesson.id,
          title: lesson.title,
        }));
        setLessons(mappedLessons);
      } catch (error: any) {
        await showAlert({
          title: "Dersler yüklenemedi",
          message: error?.message || "Ders listesi alınamadı.",
          type: "error",
        });
      } finally {
        setLoadingLessons(false);
      }
    };

    void loadLessons();
  }, [showAlert]);

  useEffect(() => {
    if (questionMode !== "context") return;
    if (form.lessonId || !lessons[0]?.id) return;

    setForm((prev) => ({
      ...prev,
      lessonId: prev.lessonId || lessons[0].id,
    }));
  }, [form.lessonId, lessons, questionMode]);

  useEffect(() => {
    sessionRestoredRef.current = false;
    handledSessionTaskIdRef.current = null;

    const restoredSession = restoreMaarifWizardSession(routeRecordId);
    if (restoredSession) {
      sessionRestoredRef.current = true;
      applyWizardSnapshotRef.current?.(restoredSession.snapshot, restoredSession.transient);
      setPdfJob(restoredSession.snapshot.pdfJob || restoredSession.pendingTask?.job || null);
      applyPendingTaskLoadingStateRef.current?.(restoredSession.pendingTask);

      if (restoredSession.pendingTask && restoredSession.pendingTask.status !== "running") {
        handledSessionTaskIdRef.current = restoredSession.pendingTask.id;
        if (restoredSession.pendingTask.status === "completed") {
          void handleCompletedSessionTaskRef.current?.(restoredSession.pendingTask);
        } else {
          void handleFailedSessionTaskRef.current?.(restoredSession.pendingTask);
        }
      }
    }

    const unsubscribe = subscribeMaarifWizardSession((session) => {
      if (!session) {
        setPdfJob(null);
        applyPendingTaskLoadingState(null);
        return;
      }

      if (session.recordId !== routeRecordId) return;

      latestSnapshotRef.current = session.snapshot;
      latestTransientRef.current = session.transient;
      setPdfJob(session.snapshot.pdfJob || session.pendingTask?.job || null);
      applyPendingTaskLoadingStateRef.current?.(session.pendingTask);

      if (!session.pendingTask || session.pendingTask.status === "running") return;
      if (handledSessionTaskIdRef.current === session.pendingTask.id) return;

      handledSessionTaskIdRef.current = session.pendingTask.id;
      if (session.pendingTask.status === "completed") {
        void handleCompletedSessionTaskRef.current?.(session.pendingTask);
      } else {
        void handleFailedSessionTaskRef.current?.(session.pendingTask);
      }
    });

    return unsubscribe;
  }, [routeRecordId]);

  useEffect(() => {
    if (!recordId) return;
    if (sessionRestoredRef.current) return;

    const loadRecord = async () => {
      setLoadingRecord(true);
      try {
        const record = await ApiService.getMaarifQuestionRecord(recordId);
        setForm({
          title: record.title,
          lessonId: record.lessonId || "",
          gradeLabel: record.gradeLabel || "7. Sınıf",
          difficulty: record.difficulty || "Orta",
          lengthPreset: record.content?.generationSettings?.lengthPreset || "Orta",
          targetWordCount: record.content?.generationSettings?.targetWordCount
            ? String(record.content.generationSettings.targetWordCount)
            : "",
          wantsVisual: Boolean(record.content?.generationSettings?.wantsVisual),
          customPrompt: record.content?.generationSettings?.customPrompt || "",
          gainText: record.gainText || "",
          processComponentCode: record.processComponentCode || MAARIF_PROCESS_COMPONENTS[0].code,
        });
        setContextText(record.content?.context?.text || "");
        setContextRationale(record.content?.context?.rationale || "");
        setQuestion(record.content?.question || null);
        setMultiQuestions(record.content?.multiQuestions || []);
        setDetectedMetadata(record.content?.detectedMetadata || null);
        setQuestionSourceAnalysis(record.content?.questionSourceAnalysis || null);
        setQuestionBankExport(record.content?.questionBankExport || null);
        setQuestionMode(
          record.content?.sourceAsset?.type === "pdf"
            ? record.content?.sourceAsset?.transformMode === "contextual"
              ? "pdf_contextual"
              : "pdf"
            : record.content?.questionSourceAnalysis
              ? "image"
              : "context"
        );
        setPdfFileName(record.content?.sourceAsset?.type === "pdf" ? record.content?.sourceAsset?.fileName || "" : "");
        setSelectedPdfPages(
          record.content?.sourceAsset?.type === "pdf" && Array.isArray(record.content?.sourceAsset?.selectedPages)
            ? record.content.sourceAsset.selectedPages
            : []
        );
        setPdfSummary(
          record.content?.sourceAsset?.type === "pdf"
            ? {
                questionCount: getQuestionCollectionCount(record.content?.multiQuestions),
                notes: "",
              }
            : null
        );
        setPdfJob(null);
        setAuditReport(record.content?.auditReport || null);
        setRewriteQuestion(record.content?.rewriteQuestion || null);
        setWorkflowSource(record.workflowSource);
        setModelInfo(record.content?.modelInfo || {});
        setRecordMeta({ createdAt: record.createdAt, updatedAt: record.updatedAt });
        setExternalQuestionText(record.content?.auditReport?.originalQuestionSummary || "");
        setAuditMode(record.workflowSource === "audited_upload" ? "external" : "generated");
        setCurrentStep(
          record.content?.sourceAsset?.type === "pdf"
            ? 2
            : record.content?.auditReport
              ? 3
              : record.content?.question
                ? 2
                : 1
        );
      } catch (error: any) {
        await showAlert({
          title: "Kayıt yüklenemedi",
          message: error?.message || "Kayıt bilgisi alınamadı.",
          type: "error",
        });
        navigate("/maarif-question-wizard");
      } finally {
        setLoadingRecord(false);
      }
    };

    void loadRecord();
  }, [navigate, recordId, showAlert]);

  useEffect(() => {
    const snapshot = buildWizardSnapshot();
    const transient = buildWizardTransientAssets();
    latestSnapshotRef.current = snapshot;
    latestTransientRef.current = transient;

    if (!getMaarifWizardSessionForRoute(routeRecordId)) return;
    syncMaarifWizardSessionSnapshot({
      recordId: routeRecordId,
      snapshot,
      transient,
    });
  }, [buildWizardSnapshot, buildWizardTransientAssets, routeRecordId]);

  useEffect(() => {
    if (isPdfQuestionMode(questionMode) && currentStep !== 2) {
      setCurrentStep(2);
    }
  }, [currentStep, questionMode]);

  useEffect(() => {
    if (!isPdfMode || form.difficulty === "Orta") return;
    setForm((prev) => ({ ...prev, difficulty: "Orta" }));
  }, [form.difficulty, isPdfMode]);

  useEffect(() => {
    return () => {
      if (skipSessionPersistOnLeaveRef.current) {
        clearMaarifWizardSession(routeRecordId);
        if (questionImagePreview?.startsWith("blob:")) {
          URL.revokeObjectURL(questionImagePreview);
        }
        if (auditImagePreview?.startsWith("blob:")) {
          URL.revokeObjectURL(auditImagePreview);
        }
        return;
      }

      const snapshot = latestSnapshotRef.current || buildWizardSnapshot();
      const transient = latestTransientRef.current || buildWizardTransientAssets();
      const activeSession = getMaarifWizardSessionForRoute(routeRecordId);
      const shouldKeep = hasMeaningfulWizardState(snapshot, transient, activeSession);
      const kept = persistMaarifWizardSessionOnLeave({
        recordId: routeRecordId,
        snapshot,
        transient,
        shouldKeep,
      });

      if (!kept) {
        if (questionImagePreview?.startsWith("blob:")) {
          URL.revokeObjectURL(questionImagePreview);
        }
        if (auditImagePreview?.startsWith("blob:")) {
          URL.revokeObjectURL(auditImagePreview);
        }
      }
    };
  }, [
    auditImagePreview,
    buildWizardSnapshot,
    buildWizardTransientAssets,
    hasMeaningfulWizardState,
    questionImagePreview,
    routeRecordId,
  ]);

  const handleQuestionImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    if (questionImagePreview) {
      URL.revokeObjectURL(questionImagePreview);
    }
    setQuestionImageFile(file);
    setQuestionImagePreview(file ? URL.createObjectURL(file) : "");
  };

  const handlePdfFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    if (file && !canUseQuestionMode(questionMode)) {
      event.currentTarget.value = "";
      await showAlert({
        title: "Yetki Hatası",
        message: "Bu PDF çevirme modu için yetkiniz yok.",
        type: "warning",
      });
      return;
    }

    setPdfJob(null);
    setPdfFile(file);
    setPdfFileName(file?.name || "");
    setSelectedPdfPages([]);
    setPdfPageCount(0);
    setPdfSummary(null);

    if (!file) return;

    setPdfMetadataLoading(true);
    try {
      const result = await ApiService.getMaarifQuestionPdfMetadata(file);
      if (!(Number(result.pageCount) > 0)) {
        throw new Error("PDF sayfa sayısı okunamadı.");
      }
      setPdfPageCount(result.pageCount || 0);
      setSelectedPdfPages([]);
      setPdfPageModalOpen(true);
    } catch (error: any) {
      await showAlert({
        title: "PDF okunamadı",
        message: error?.message || "PDF sayfa bilgileri alınamadı.",
        type: "error",
      });
    } finally {
      setPdfMetadataLoading(false);
    }
  };

  const handleAuditImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    if (auditImagePreview) {
      URL.revokeObjectURL(auditImagePreview);
    }
    setAuditImage(file);
    setAuditImagePreview(file ? URL.createObjectURL(file) : "");
  };

  const switchQuestionMode = (mode: QuestionMode) => {
    if (!canUseQuestionMode(mode)) {
      showToast(isPdfQuestionMode(mode) ? "Bu PDF çevirme modu için yetkiniz yok." : "Bu soru üretme modu için yetkiniz yok.", "error");
      return;
    }

    setQuestionMode(mode);
    setCurrentStep(mode === "context" ? 1 : 2);
  };

  const openPdfPageModal = async () => {
    if (!pdfFile) return;

    if (!pdfPageCount) {
      setPdfMetadataLoading(true);
      try {
        const result = await ApiService.getMaarifQuestionPdfMetadata(pdfFile);
        if (!(Number(result.pageCount) > 0)) {
          throw new Error("PDF sayfa sayısı okunamadı.");
        }
        setPdfPageCount(result.pageCount || 0);
      } catch (error: any) {
        await showAlert({
          title: "PDF okunamadı",
          message: error?.message || "PDF sayfa bilgileri alınamadı.",
          type: "error",
        });
        return;
      } finally {
        setPdfMetadataLoading(false);
      }
    }

    setPdfPageModalOpen(true);
  };

  const ensureRequiredFields = async () => {
    if (!form.lessonId || !form.gradeLabel || !form.gainText.trim() || !form.processComponentCode) {
      await showAlert({
        title: "Eksik Bilgi",
        message: "Ders, sınıf seviyesi, kazanım ve süreç bileşeni seçimi zorunludur.",
        type: "warning",
      });
      return false;
    }

    return true;
  };

  const loadAdminPrompts = async () => {
    setAdminPromptsLoading(true);
    try {
      const configs = await ApiService.getMaarifPromptConfigs();
      setAdminPromptConfigs(configs);
    } finally {
      setAdminPromptsLoading(false);
    }
  };

  const handleAdminUnlock = () => {
    if (adminPassword === "1903") {
      setAdminUnlocked(true);
      setAdminPasswordError(false);
      loadAdminPrompts();
    } else {
      setAdminPasswordError(true);
    }
  };

  const handleAdminEdit = (config: MaarifPromptConfig) => {
    setAdminEditingKey(config.key);
    setAdminEditingContent(config.content ?? config.defaultContent);
  };

  const handleAdminSave = async () => {
    if (!adminEditingKey) return;
    setAdminSaving(true);
    try {
      const updated = await ApiService.updateMaarifPromptConfig(adminEditingKey, adminEditingContent);
      setAdminPromptConfigs(updated);
      setAdminEditingKey(null);
    } finally {
      setAdminSaving(false);
    }
  };

  const handleAdminReset = async (key: string) => {
    setAdminSaving(true);
    try {
      const updated = await ApiService.resetMaarifPromptConfig(key);
      setAdminPromptConfigs(updated);
      if (adminEditingKey === key) setAdminEditingKey(null);
    } finally {
      setAdminSaving(false);
    }
  };

  const handleGenerateContext = async () => {
    if (!(await ensureRequiredFields())) return;
    setContextLoading(true);
    handledSessionTaskIdRef.current = null;
    startMaarifWizardContextTask({
      recordId: routeRecordId,
      snapshot: buildWizardSnapshot(),
      transient: buildWizardTransientAssets(),
      payload: {
        lessonId: form.lessonId,
        gradeLabel: form.gradeLabel,
        difficulty: form.difficulty,
        lengthPreset: form.lengthPreset,
        targetWordCount: null,
        wantsVisual: form.wantsVisual,
        customPrompt: form.customPrompt.trim(),
        gainText: form.gainText.trim(),
        processComponentCode: form.processComponentCode,
      },
    });
  };

  const handleGenerateQuestion = async () => {
    if (!(await ensureRequiredFields())) return;
    if (!contextText.trim()) {
      await showAlert({
        title: "Bağlam Gerekli",
        message: "Önce bağlam üretin veya bağlam alanına metin girin.",
        type: "warning",
      });
      return;
    }

    setQuestionLoading(true);
    handledSessionTaskIdRef.current = null;
    startMaarifWizardQuestionTask({
      recordId: routeRecordId,
      snapshot: buildWizardSnapshot(),
      transient: buildWizardTransientAssets(),
      payload: {
        lessonId: form.lessonId,
        gradeLabel: form.gradeLabel,
        difficulty: form.difficulty,
        lengthPreset: form.lengthPreset,
        targetWordCount: null,
        wantsVisual: form.wantsVisual,
        customPrompt: form.customPrompt.trim(),
        gainText: form.gainText.trim(),
        processComponentCode: form.processComponentCode,
        context: contextText.trim(),
      },
    });
  };

  const handleGenerateQuestionFromImage = async () => {
    if (!questionImageFile) {
      await showAlert({
        title: "Soru Görseli Gerekli",
        message: "Önce analiz edilecek soru görselini yüklemelisiniz.",
        type: "warning",
      });
      return;
    }

    setQuestionLoading(true);
    handledSessionTaskIdRef.current = null;
    startMaarifWizardImageTask({
      recordId: routeRecordId,
      snapshot: buildWizardSnapshot(),
      transient: buildWizardTransientAssets(),
      payload: {
        lessonId: "",
        gradeLabel: "",
        difficulty: form.difficulty,
        lengthPreset: form.lengthPreset,
        targetWordCount: null,
        wantsVisual: form.wantsVisual,
        customPrompt: form.customPrompt.trim(),
        gainText: "",
        processComponentCode: "",
        context: contextText.trim(),
        image: questionImageFile,
      },
    });
  };

  const handleGenerateQuestionsFromPdf = async () => {
    if (!canUseQuestionMode(questionMode)) {
      await showAlert({
        title: "Yetki Hatası",
        message: "Bu PDF çevirme modu için yetkiniz yok.",
        type: "warning",
      });
      return;
    }

    if (!pdfFile) {
      await showAlert({
        title: "PDF Gerekli",
        message: "Çevrilecek sorular için önce bir PDF yüklemelisiniz.",
        type: "warning",
      });
      return;
    }

    if (!selectedPdfPages.length) {
      await showAlert({
        title: "Sayfa Seçimi Gerekli",
        message: "Modele gönderilecek en az bir PDF sayfası seçmelisiniz.",
        type: "warning",
      });
      return;
    }

    if (selectedPdfPages.length > MAX_PDF_PAGE_SELECTION) {
      await showAlert({
        title: "Sayfa sınırı aşıldı",
        message: `PDF'ten aynı anda en fazla ${MAX_PDF_PAGE_SELECTION} sayfa işleyebilirsiniz.`,
        type: "warning",
      });
      return;
    }

    setQuestionLoading(true);
    handledSessionTaskIdRef.current = null;
    setPdfJob(null);
    setQuestion(null);
    setRewriteQuestion(null);
    setMultiQuestions([]);
    setPdfSummary(null);
    setDetectedMetadata(null);
    setQuestionSourceAnalysis(null);
    setAuditReport(null);
    setWorkflowSource("generated");

    startMaarifWizardPdfTask({
      recordId: routeRecordId,
      snapshot: buildWizardSnapshot(),
      transient: buildWizardTransientAssets(),
      payload: {
        difficulty: form.difficulty,
        rewriteMode: pdfRewriteMode,
        lengthPreset: form.lengthPreset,
        targetWordCount: null,
        wantsVisual: form.wantsVisual,
        customPrompt: form.customPrompt.trim(),
        context: contextText.trim(),
        selectedPages: selectedPdfPages,
        pdf: pdfFile,
      },
    });
    showToast("PDF işleme arka planda başlatıldı.", "success");
  };

  const handleAudit = async () => {
    if (!(await ensureRequiredFields())) return;
    const auditQuestionText = auditMode === "generated" ? generatedQuestionText : externalQuestionText.trim();
    if (!auditQuestionText && !auditImage) {
      await showAlert({
        title: "Soru Gerekli",
        message: "Denetim için soru metni veya görsel yüklemelisiniz.",
        type: "warning",
      });
      return;
    }

    setAuditLoading(true);
    handledSessionTaskIdRef.current = null;
    startMaarifWizardAuditTask({
      recordId: routeRecordId,
      snapshot: buildWizardSnapshot(),
      transient: buildWizardTransientAssets(),
      payload: {
        lessonId: form.lessonId,
        gradeLabel: form.gradeLabel,
        difficulty: form.difficulty,
        lengthPreset: form.lengthPreset,
        targetWordCount: null,
        wantsVisual: form.wantsVisual,
        customPrompt: form.customPrompt.trim(),
        gainText: form.gainText.trim(),
        processComponentCode: form.processComponentCode,
        questionText: auditQuestionText,
        image: auditMode === "external" ? auditImage : null,
      },
    });
  };

  const handleRewrite = async () => {
    if (!(await ensureRequiredFields())) return;
    if (!auditReport) {
      await showAlert({
        title: "Denetim Gerekli",
        message: "Önce soru denetmeni ile bir rapor üretmelisiniz.",
        type: "warning",
      });
      return;
    }

    const sourceQuestionText = auditMode === "generated" ? generatedQuestionText : externalQuestionText.trim();

    setRewriteLoading(true);
    handledSessionTaskIdRef.current = null;
    startMaarifWizardRewriteTask({
      recordId: routeRecordId,
      snapshot: buildWizardSnapshot(),
      transient: buildWizardTransientAssets(),
      payload: {
        lessonId: form.lessonId,
        gradeLabel: form.gradeLabel,
        difficulty: form.difficulty,
        lengthPreset: form.lengthPreset,
        targetWordCount: null,
        wantsVisual: form.wantsVisual,
        customPrompt: form.customPrompt.trim(),
        gainText: form.gainText.trim(),
        processComponentCode: form.processComponentCode,
        questionText: sourceQuestionText,
        context: contextText.trim(),
        image: auditMode === "external" ? auditImage : null,
        auditReport,
      },
    });
  };

  const resolveCurrentRecordTitle = () =>
    form.title.trim() ||
    (isPdfMode
      ? `${pdfFileName || "PDF"} • ${pdfRewriteMode === "contextual" ? "Bağlamı Değiştirilen Çoklu Soru" : "Çoklu Soru"}`
      : buildSuggestedTitle(selectedLessonTitle, form.gainText));

  const resolveNormalizedQuestionState = () => {
    const fallbackProcessComponentCode =
      form.processComponentCode ||
      detectedMetadata?.processComponentCode ||
      MAARIF_PROCESS_COMPONENTS[0].code;

    return {
      normalizedQuestion: normalizeQuestionProcessComponent(question, fallbackProcessComponentCode),
      normalizedRewriteQuestion: normalizeQuestionProcessComponent(rewriteQuestion, fallbackProcessComponentCode),
      normalizedMultiQuestions: normalizeQuestionCollectionProcessComponents(
        isPdfMode && multiQuestions.length ? multiQuestions : [],
        fallbackProcessComponentCode
      ),
    };
  };

  const buildQuestionBankExportPayload = () => {
    const title = resolveCurrentRecordTitle();
    const { normalizedQuestion, normalizedRewriteQuestion, normalizedMultiQuestions } = resolveNormalizedQuestionState();

    return {
      recordId: isEdit ? (recordId as string) : null,
      title,
      lessonTitle: selectedLessonTitle || detectedMetadata?.lessonTitle || null,
      questionBankId: questionBankExport?.questionBankId || null,
      content: {
        question: normalizedQuestion,
        multiQuestions: isPdfMode && normalizedMultiQuestions.length ? normalizedMultiQuestions : null,
        detectedMetadata,
        rewriteQuestion: normalizedRewriteQuestion,
      },
    };
  };

  const handleSave = async () => {
    if (questionMode === "context" && !(await ensureRequiredFields())) return;
    const title = resolveCurrentRecordTitle();
    const { normalizedQuestion, normalizedRewriteQuestion, normalizedMultiQuestions } = resolveNormalizedQuestionState();

    const payload = {
      title,
      lessonId: isPdfMode ? null : form.lessonId || null,
      lessonTitle: isPdfMode ? null : selectedLessonTitle,
      gradeLabel: isPdfMode ? detectedMetadata?.gradeLabel || null : form.gradeLabel || detectedMetadata?.gradeLabel || null,
      difficulty: form.difficulty,
      gainText: isPdfMode ? detectedMetadata?.gainText || null : form.gainText.trim() || detectedMetadata?.gainText || null,
      processComponentCode: isPdfMode ? detectedMetadata?.processComponentCode || null : form.processComponentCode || detectedMetadata?.processComponentCode || null,
      workflowSource,
      content: {
        generationSettings: {
          lengthPreset: form.lengthPreset,
          targetWordCount: null,
          wantsVisual: form.wantsVisual,
          customPrompt: form.customPrompt.trim(),
        },
        context: contextText.trim() || contextRationale.trim() ? { text: contextText.trim(), rationale: contextRationale.trim() } : null,
        question: normalizedQuestion,
        multiQuestions: isPdfMode && normalizedMultiQuestions.length ? normalizedMultiQuestions : null,
        detectedMetadata,
        questionSourceAnalysis,
        auditReport,
        rewriteQuestion: normalizedRewriteQuestion,
        questionBankExport,
        sourceAsset:
          questionMode === "image"
            ? { type: "image" as const, fileName: questionImageFile?.name || null }
            : isPdfMode
              ? {
                  type: "pdf" as const,
                  fileName: pdfFileName || null,
                  selectedPages: selectedPdfPages,
                  transformMode: pdfRewriteMode,
                }
              : null,
        modelInfo,
      },
    };

    if (!payload.content.question && !payload.content.rewriteQuestion && !getQuestionCollectionCount(payload.content.multiQuestions)) {
      await showAlert({
        title: "Soru Gerekli",
        message: "Kaydetmeden önce en az bir soru üretmelisiniz.",
        type: "warning",
      });
      return;
    }

    setSaving(true);
    try {
      const result = isEdit
        ? await ApiService.updateMaarifQuestionRecord(recordId as string, payload)
        : await ApiService.saveMaarifQuestionRecord(payload);

      clearMaarifWizardSession(routeRecordId);
      setForm((prev) => ({ ...prev, title: result.title }));
      setRecordMeta({ createdAt: result.createdAt, updatedAt: result.updatedAt });
      showToast(isEdit ? "Kayıt güncellendi." : "Kayıt oluşturuldu.", "success");
      if (!isEdit) {
        navigate(`/maarif-question-wizard/${result.id}`, { replace: true });
      }
    } catch (error: any) {
      await showAlert({
        title: isEdit ? "Kayıt güncellenemedi" : "Kayıt oluşturulamadı",
        message: error?.message || "Kayıt işlemi sırasında bir hata oluştu.",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleExportToQuestionBank = async () => {
    if (questionMode === "context" && !(await ensureRequiredFields())) return;

    const exportPayload = buildQuestionBankExportPayload();
    const exportQuestionCount =
      (Array.isArray(exportPayload.content.multiQuestions) ? exportPayload.content.multiQuestions.length : 0) ||
      (exportPayload.content.rewriteQuestion || exportPayload.content.question ? 1 : 0);

    if (!exportQuestionCount) {
      await showAlert({
        title: "Soru Gerekli",
        message: "Dizgiciye aktarmadan önce en az bir soru üretmelisiniz.",
        type: "warning",
      });
      return;
    }

    setExportingQuestionBank(true);
    try {
      const result = await ApiService.exportMaarifQuestionToQuestionBank(exportPayload);
      const nextQuestionBankExport = {
        questionBankId: result.questionBankId,
        exportedAt: result.exportedAt,
      };

      setQuestionBankExport(nextQuestionBankExport);
      if (isEdit) {
        setRecordMeta((prev) =>
          prev
            ? {
                ...prev,
                updatedAt: result.exportedAt,
              }
            : prev
        );
      }
      showToast("Dizgici taslağı hazırlandı.", "success");
      navigate(`/question-bank-indesign/${result.questionBankId}/edit`);
    } catch (error: any) {
      await showAlert({
        title: "Dizgiciye aktarılamadı",
        message: error?.message || "Soru Dizgici taslağına dönüştürülemedi.",
        type: "error",
      });
    } finally {
      setExportingQuestionBank(false);
    }
  };

  const handleDownloadPdf = async () => {
    const exportQuestions = multiQuestions.length ? multiQuestions : [];
    const exportQuestion = rewriteQuestion || question;
    if (!exportQuestion && !exportQuestions.length) {
      await showAlert({
        title: "Soru Gerekli",
        message: "PDF indirmeden önce en az bir soru üretmelisiniz.",
        type: "warning",
      });
      return;
    }

    const exportTitle =
      form.title.trim() ||
      (exportQuestions.length
        ? `${pdfFileName || "PDF"} • ${pdfRewriteMode === "contextual" ? "Bağlamı Değiştirilen Çoklu Soru" : "Çoklu Soru"}`
        : buildSuggestedTitle(selectedLessonTitle, form.gainText));
    const buildPerQuestionPdfMetadataItems = (targetQuestion: MaarifSingleQuestion): QuestionPdfMetadataItem[] =>
      [
        {
          label: "Kazanım",
          value: String(targetQuestion.gainText || "").trim() || detectedMetadata?.gainText || "",
        },
        {
          label: "Süreç Bileşeni",
          value:
            targetQuestion.measuredProcessComponentCode && targetQuestion.measuredProcessComponentLabel
              ? `${targetQuestion.measuredProcessComponentCode} - ${targetQuestion.measuredProcessComponentLabel}`
              : detectedMetadata?.processComponentCode && detectedMetadata?.processComponentLabel
                ? `${detectedMetadata.processComponentCode} - ${detectedMetadata.processComponentLabel}`
                : "",
        },
      ].filter((item) => item.value);
    const exportMetadataItems: QuestionPdfMetadataItem[] = exportQuestions.length
      ? []
      : [
          { label: "Ders", value: selectedLessonTitle || detectedMetadata?.lessonTitle || "Belirtilmeyen Ders" },
          { label: "Sınıf", value: form.gradeLabel || detectedMetadata?.gradeLabel || "-" },
          { label: "Zorluk", value: form.difficulty },
          { label: "Kazanım", value: form.gainText.trim() || detectedMetadata?.gainText || "-" },
          {
            label: "Süreç Bileşeni",
            value:
              summaryProcessComponentText ||
              (exportQuestion ? `${exportQuestion.measuredProcessComponentCode} - ${exportQuestion.measuredProcessComponentLabel}` : ""),
          },
        ];

    setDownloadingPdf(true);
    try {
      let questionsForPdf = exportQuestions;
      let questionForPdf = exportQuestion as MaarifSingleQuestion | null;
      const currentOutputHasImages = questionsForPdf.length
        ? questionsForPdf.some((item) => questionHasRenderableImages(item))
        : questionHasRenderableImages(questionForPdf);

      if (!currentOutputHasImages && questionBankExport?.questionBankId) {
        try {
          const questionBank = await ApiService.getQuestionBank(questionBankExport.questionBankId);
          const mappedQuestionBankQuestions = mapQuestionBankQuestionsToMaarifQuestions(
            questionBank,
            form.processComponentCode || detectedMetadata?.processComponentCode,
            detectedMetadata?.processComponentLabel,
            form.gainText.trim() || detectedMetadata?.gainText
          );
          const questionBankOutputHasImages = mappedQuestionBankQuestions.some((item) => questionHasRenderableImages(item));

          if (questionBankOutputHasImages) {
            if (questionsForPdf.length && mappedQuestionBankQuestions.length === questionsForPdf.length) {
              questionsForPdf = questionsForPdf.map((item, index) =>
                mergeRenderableImagesIntoQuestion(item, mappedQuestionBankQuestions[index])
              );
            } else if (!questionsForPdf.length && mappedQuestionBankQuestions.length) {
              questionForPdf = questionForPdf
                ? mergeRenderableImagesIntoQuestion(questionForPdf, mappedQuestionBankQuestions[0])
                : mappedQuestionBankQuestions[0];
            }
          }
        } catch (error) {
          console.warn("Dizgici görselleri PDF çıktısına taşınamadı.", error);
        }
      }

      const pageImages = questionsForPdf.length
        ? await renderQuestionCollectionPdfPages({
            title: exportTitle,
            metadataItems: exportMetadataItems,
            getMetadataItems: buildPerQuestionPdfMetadataItems,
            workflowSourceLabel: "",
            questions: questionsForPdf,
          })
        : await renderQuestionPdfPages({
            title: exportTitle,
            metadataItems: exportMetadataItems,
            workflowSourceLabel: workflowSource === "audited_upload" ? "Denetim + yeniden yazım" : "Sihirbaz üretimi",
            question: questionForPdf as MaarifSingleQuestion,
          });
      const pdfBlob = buildPdfFromJpegPages(pageImages);
      const blobUrl = URL.createObjectURL(pdfBlob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `${sanitizeFileName(exportTitle)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
      showToast("PDF indirildi.", "success");
    } catch (error: any) {
      await showAlert({
        title: "PDF indirilemedi",
        message: error?.message || "PDF hazırlanırken bir hata oluştu.",
        type: "error",
      });
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleDelete = async () => {
    if (!recordId) return;
    const confirmed = await showConfirm({
      title: "Kaydı Sil",
      message: "Bu kayıt kalıcı olarak silinecek. Devam etmek istiyor musunuz?",
      confirmText: "Sil",
      confirmButtonVariant: "danger",
    });

    if (!confirmed) return;

    setDeleting(true);
    try {
      await ApiService.deleteMaarifQuestionRecord(recordId);
      clearMaarifWizardSession(routeRecordId);
      showToast("Kayıt silindi.", "success");
      navigate("/maarif-question-wizard");
    } catch (error: any) {
      await showAlert({
        title: "Silme Hatası",
        message: error?.message || "Kayıt silinemedi.",
        type: "error",
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleReturnToRecords = () => {
    skipSessionPersistOnLeaveRef.current = true;
    clearMaarifWizardSession(routeRecordId);
    navigate("/maarif-question-wizard", { replace: true });
  };

  if ((isEdit && !canList) || (!isEdit && !canCreate)) {
    return (
      <div className="min-h-[calc(100vh-120px)] flex items-center justify-center px-6">
        <div className="max-w-md rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-sm">
          <div className="text-lg font-semibold text-gray-900">Bu alana erişiminiz yok</div>
          <div className="mt-2 text-sm text-gray-600">Maarif Soru Sihirbazı çalışma alanını kullanma yetkiniz bulunmuyor.</div>
        </div>
      </div>
    );
  }

  const stepItems =
    isPdfMode
      ? [{ id: 2, title: "PDF Soru Dönüştürücü", icon: FileText }]
      : [
          { id: 1, title: "Bağlam Üretici", icon: BookOpen },
          { id: 2, title: "Soru Üretici", icon: Sparkles },
          { id: 3, title: "Soru Denetmeni", icon: CheckSquare },
        ];

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-white overflow-y-auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        <div className="rounded-[28px] border border-white/70 bg-white/90 shadow-sm backdrop-blur-sm overflow-hidden">
          <div className="px-6 py-6 lg:px-8 bg-gradient-to-r from-emerald-700 via-emerald-600 to-teal-600 text-white">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
              <div className="max-w-3xl">
                <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold tracking-wide uppercase">
                  Maarif Soru Sihirbazı
                </div>
                <h1 className="mt-3 text-2xl lg:text-3xl font-semibold">
                  {isEdit ? "Kayıt Detayı" : isPdfMode ? "Yeni Çoklu PDF Akışı" : "Yeni Soru Akışı"}
                </h1>
                <p className="mt-2 text-sm lg:text-base text-emerald-50/90">
                  {isPdfMode
                    ? pdfRewriteMode === "contextual"
                      ? "PDF içindeki birden fazla soruyu aynı kazanım ve çözüm mantığında, ama yeni denklem ve veri kurgusuyla TYMM/Maarif uyumlu sürümlere dönüştür."
                      : "PDF içindeki birden fazla soruyu denklem ve çözüm modelini bozmadan, sayıları ve yüzey anlatımını yenileyerek TYMM/Maarif uyumlu sürümlere dönüştür."
                    : "Bağlam oluştur, tek soru üret, TYMM/Maarif uyumunu denetle ve gerekirse aynı mantıkta daha güçlü bir soru yeniden yazdır."}
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={handleReturnToRecords}
                  className="px-4 py-2 rounded-xl border border-white/30 bg-white/10 text-sm font-medium hover:bg-white/20 transition"
                >
                  Kayıtlara Dön
                </button>
                {isEdit && canDelete ? (
                  <button
                    onClick={handleDelete}
                    disabled={deleting}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-rose-500/90 text-white text-sm font-medium hover:bg-rose-500 transition disabled:opacity-70"
                  >
                    {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    Sil
                  </button>
                ) : null}
                {(isEdit ? canUpdate : canCreate) ? (
                  <button
                    onClick={handleDownloadPdf}
                    disabled={downloadingPdf || loadingLessons || loadingRecord}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-white/30 bg-white/10 text-white text-sm font-medium hover:bg-white/20 transition disabled:opacity-70"
                  >
                    {downloadingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                    İndir
                  </button>
                ) : null}
                {canExportToQuestionBank ? (
                  <button
                    onClick={handleExportToQuestionBank}
                    disabled={exportingQuestionBank || loadingLessons || loadingRecord}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-white/30 bg-white/10 text-white text-sm font-medium hover:bg-white/20 transition disabled:opacity-70"
                  >
                    {exportingQuestionBank ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                    {questionBankExport?.questionBankId ? "Dizgiciyi Güncelle" : "Dizgiciye Aktar"}
                  </button>
                ) : null}
                {(isEdit ? canUpdate : canCreate) ? (
                  <button
                    onClick={handleSave}
                    disabled={saving || loadingLessons || loadingRecord}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white text-emerald-700 text-sm font-semibold hover:bg-emerald-50 transition disabled:opacity-70"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    {isEdit ? "Güncelle" : "Kaydet"}
                  </button>
                ) : null}
              </div>
            </div>
          </div>

          <div className="px-6 py-5 lg:px-8 border-b border-gray-100 bg-white">
            <div className="flex flex-wrap gap-2 mb-5">
              <button
                onClick={() => setPanelTab("workflow")}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-medium transition ${
                  panelTab === "workflow"
                    ? "border-emerald-300 bg-emerald-50 text-emerald-900"
                    : "border-gray-200 bg-white text-gray-700"
                }`}
              >
                <Sparkles className="w-4 h-4" />
                Akış
              </button>
              <Link
                to="/maarif-question-wizard/curriculum-test"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 bg-white text-sm font-medium text-gray-700 transition hover:bg-gray-50 hover:border-gray-300"
              >
                <FlaskConical className="w-4 h-4" />
                Müfredat Test (V2)
              </Link>
              <button
                onClick={() => setPanelTab("settings")}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-medium transition ${
                  panelTab === "settings"
                    ? "border-emerald-300 bg-emerald-50 text-emerald-900"
                    : "border-gray-200 bg-white text-gray-700"
                }`}
              >
                <Settings2 className="w-4 h-4" />
                Ayarlar
              </button>
              {isSuperAdmin && (
                <button
                  onClick={() => setPanelTab("admin")}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-medium transition ${
                    panelTab === "admin"
                      ? "border-amber-300 bg-amber-50 text-amber-900"
                      : "border-gray-200 bg-white text-gray-700"
                  }`}
                >
                  <ShieldCheck className="w-4 h-4" />
                  Süper Admin
                </button>
              )}
            </div>

            {panelTab === "workflow" ? (
              <div className="grid grid-cols-1 xl:grid-cols-12 gap-5 items-start">
                <div className="xl:col-span-12">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Kayıt Başlığı</label>
                  <input
                    value={form.title}
                    onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
                    placeholder={
                      isPdfMode
                        ? `${pdfFileName || "PDF"} • ${pdfRewriteMode === "contextual" ? "Bağlamı Değiştirilen Çoklu Soru" : "Çoklu Soru"}`
                        : buildSuggestedTitle(selectedLessonTitle, form.gainText)
                    }
                    className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>

                {questionMode === "context" ? (
                  <>
                    <div className="xl:col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Ders</label>
                      <select
                        value={form.lessonId}
                        onChange={(event) => setForm((prev) => ({ ...prev, lessonId: event.target.value }))}
                        disabled={loadingLessons || loadingRecord}
                        className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      >
                        {lessons.map((lesson) => (
                          <option key={lesson.id} value={lesson.id}>
                            {lesson.title}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="xl:col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Sınıf Seviyesi</label>
                      <select
                        value={form.gradeLabel}
                        onChange={(event) => setForm((prev) => ({ ...prev, gradeLabel: event.target.value }))}
                        className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      >
                        {MAARIF_GRADE_OPTIONS.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="xl:col-span-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Beceri Türü</label>
                      <select
                        value={form.processComponentCode}
                        onChange={(event) => setForm((prev) => ({ ...prev, processComponentCode: event.target.value }))}
                        className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      >
                        {["Problem Çözme", "Sorgulama"].map((group) => (
                          <optgroup key={group} label={group}>
                            {MAARIF_PROCESS_COMPONENTS.filter((item) => item.group === group).map((item) => (
                              <option key={item.code} value={item.code}>
                                {item.code} - {item.label}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                    </div>
                    <div className="xl:col-span-12">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Kazanım</label>
                      <textarea
                        value={form.gainText}
                        onChange={(event) => setForm((prev) => ({ ...prev, gainText: event.target.value }))}
                        rows={3}
                        className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        placeholder="Örn. Veriye dayalı çıkarım yaparak uygun çözüm yolunu belirler."
                      />
                    </div>
                  </>
                ) : questionMode === "image" ? (
                  <div className="xl:col-span-12 rounded-2xl border border-sky-200 bg-sky-50/60 px-4 py-4 text-sm text-sky-900">
                    Bu modda ders, sınıf seviyesi, kazanım ve beceri türü bilgisi görüntüden çözümlenir.
                    Sistem yüklenen soruyu TYMM/Maarif uyumlu yeni bir sürüme taşırken mantığı korur, örnek adları ve şıkları yüzeysel olarak farklılaştırır.
                  </div>
                ) : (
                  <div className="xl:col-span-12 rounded-2xl border border-sky-200 bg-sky-50/60 px-4 py-4 text-sm text-sky-900">
                    PDF modunda birden fazla soru aynı işlemde çevrilir. Ana kayıt çoklu soru olarak saklanır ve liste ekranında soru sayısı gösterilir.
                  </div>
                )}
              </div>
            ) : panelTab === "settings" ? (
              <div className="grid grid-cols-1 xl:grid-cols-12 gap-5 items-start">
                <div className="xl:col-span-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Zorluk</label>
                  <select
                    value={form.difficulty}
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, difficulty: event.target.value as MaarifQuestionDifficulty }))
                    }
                    disabled={isDifficultyLocked}
                    className={`w-full rounded-xl border px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                      isDifficultyLocked ? "border-gray-200 bg-gray-100 text-gray-500 cursor-not-allowed" : "border-gray-300 bg-white"
                    }`}
                  >
                    {MAARIF_DIFFICULTY_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                  {isDifficultyLocked ? (
                    <div className="mt-2 text-xs text-gray-500">PDF çoklu çevir modunda zorluk sabit olarak Orta kullanılır.</div>
                  ) : null}
                </div>
                <div className="xl:col-span-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Uzunluk</label>
                  <select
                    value={form.lengthPreset}
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, lengthPreset: event.target.value as MaarifQuestionLengthPreset }))
                    }
                    className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    {MAARIF_LENGTH_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label} - {option.description}
                      </option>
                    ))}
                  </select>
                  <div className="mt-2 text-xs text-gray-500">
                    {form.lengthPreset}: {getLengthDescription(form.lengthPreset)}
                  </div>
                </div>
                <div className="xl:col-span-5">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Görsel İsteği</label>
                  <label className="flex min-h-[52px] items-center gap-3 rounded-xl border border-gray-300 bg-white px-4 py-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.wantsVisual}
                      onChange={(event) => setForm((prev) => ({ ...prev, wantsVisual: event.target.checked }))}
                      className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    <span className="text-sm font-medium text-gray-800">Destek görseli üret</span>
                  </label>
                  <div className="mt-2 text-xs text-gray-500">Grafik sorularında sistem gerekli destek görselini otomatik üretir.</div>
                </div>
                <div className="xl:col-span-12">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Kişiselleştirilmiş Prompt</label>
                  <textarea
                    value={form.customPrompt}
                    onChange={(event) => setForm((prev) => ({ ...prev, customPrompt: event.target.value }))}
                    rows={5}
                    className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="Örn. Türkçe sorularda daha yalın dil kullan, bağlamı okul yaşamına yaklaştır, seçeneklerde benzer uzunluk koru."
                  />
                  <div className="mt-2 text-xs text-gray-500">
                    Bu alan soru üretimi, görselden yeniden kurma, denetim, yeniden yazım ve PDF çevirme promptlarına aynen eklenir.
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-5">
                {!adminUnlocked ? (
                  <div className="flex flex-col items-center justify-center gap-4 py-10">
                    <ShieldCheck className="w-10 h-10 text-amber-500" />
                    <p className="text-sm text-gray-600">Bu bölüme erişmek için süper admin şifresini girin.</p>
                    <div className="flex gap-2">
                      <input
                        type="password"
                        value={adminPassword}
                        onChange={(e) => { setAdminPassword(e.target.value); setAdminPasswordError(false); }}
                        onKeyDown={(e) => e.key === "Enter" && handleAdminUnlock()}
                        placeholder="Şifre"
                        className={`rounded-xl border px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 ${adminPasswordError ? "border-red-400" : "border-gray-300"}`}
                      />
                      <button
                        onClick={handleAdminUnlock}
                        className="px-4 py-2 rounded-xl bg-amber-500 text-white text-sm font-medium hover:bg-amber-600"
                      >
                        Giriş
                      </button>
                    </div>
                    {adminPasswordError && <p className="text-xs text-red-500">Hatalı şifre.</p>}
                  </div>
                ) : adminPromptsLoading ? (
                  <div className="flex items-center gap-2 py-8 justify-center text-sm text-gray-500">
                    <Loader2 className="w-5 h-5 animate-spin" /> Yükleniyor...
                  </div>
                ) : (
                  <div className="space-y-6">
                    {adminPromptConfigs.map((config) => (
                      <div key={config.key} className="rounded-2xl border border-amber-200 bg-amber-50/40 p-5 space-y-3">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <div className="font-semibold text-gray-900 text-sm">{config.label}</div>
                            <div className="text-xs text-gray-500 mt-0.5">{config.description}</div>
                            {config.isCustomized && (
                              <span className="inline-block mt-1 px-2 py-0.5 rounded-full bg-amber-200 text-amber-800 text-xs font-medium">Özelleştirilmiş</span>
                            )}
                          </div>
                          <div className="flex gap-2 flex-shrink-0">
                            {adminEditingKey !== config.key && (
                              <button
                                onClick={() => handleAdminEdit(config)}
                                disabled={adminSaving}
                                className="px-3 py-1.5 rounded-lg bg-amber-500 text-white text-xs font-medium hover:bg-amber-600 disabled:opacity-60"
                              >
                                Düzenle
                              </button>
                            )}
                            {config.isCustomized && adminEditingKey !== config.key && (
                              <button
                                onClick={() => handleAdminReset(config.key)}
                                disabled={adminSaving}
                                className="px-3 py-1.5 rounded-lg border border-red-300 text-red-600 text-xs font-medium hover:bg-red-50 disabled:opacity-60"
                              >
                                Varsayılana Dön
                              </button>
                            )}
                          </div>
                        </div>
                        {adminEditingKey === config.key ? (
                          <div className="space-y-2">
                            <textarea
                              value={adminEditingContent}
                              onChange={(e) => setAdminEditingContent(e.target.value)}
                              rows={14}
                              className="w-full rounded-xl border border-amber-300 px-4 py-3 text-xs font-mono resize-y focus:outline-none focus:ring-2 focus:ring-amber-400"
                            />
                            <div className="flex gap-2">
                              <button
                                onClick={handleAdminSave}
                                disabled={adminSaving}
                                className="px-4 py-2 rounded-xl bg-amber-500 text-white text-sm font-medium hover:bg-amber-600 disabled:opacity-60 inline-flex items-center gap-1.5"
                              >
                                {adminSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                                Kaydet
                              </button>
                              <button
                                onClick={() => setAdminEditingKey(null)}
                                disabled={adminSaving}
                                className="px-4 py-2 rounded-xl border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 disabled:opacity-60"
                              >
                                İptal
                              </button>
                            </div>
                          </div>
                        ) : (
                          <pre className="text-xs font-mono whitespace-pre-wrap text-gray-700 bg-white rounded-xl border border-gray-200 p-4 max-h-48 overflow-y-auto">
                            {config.content ?? config.defaultContent}
                          </pre>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {recordMeta ? (
              <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-gray-500">
                <span>Oluşturuldu: {formatDate(recordMeta.createdAt)}</span>
                <span>Güncellendi: {formatDate(recordMeta.updatedAt)}</span>
                <span>
                  Kaynak: {workflowSource === "audited_upload" ? "Denetim + yeniden yazım" : "Sihirbaz üretimi"}
                </span>
                <span>Soru Sayısı: {questionCount}</span>
              </div>
            ) : null}
          </div>
        </div>

        <div className="space-y-6">
            <div className="rounded-3xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap gap-3">
                {stepItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = currentStep === item.id;
                  const isDone = currentStep > item.id;
                  return (
                    <button
                      key={item.id}
                      onClick={() => setCurrentStep(item.id)}
                      className={`flex-1 min-w-[180px] rounded-2xl border px-4 py-4 text-left transition ${
                        isActive
                          ? "border-emerald-400 bg-emerald-50 text-emerald-900"
                          : isDone
                            ? "border-teal-200 bg-teal-50 text-teal-900"
                            : "border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-2xl bg-white border border-current/10 flex items-center justify-center">
                          <Icon className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="text-xs uppercase tracking-wide opacity-70">Adım {item.id}</div>
                          <div className="font-semibold">{item.title}</div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {currentStep === 1 ? (
              <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
                <div className="flex items-center justify-between gap-4 mb-5">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">Adım 1: Bağlam Üretici</h2>
                    <p className="mt-1 text-sm text-gray-600">
                      Seçilen ders, kazanım ve süreç bileşenine uygun otantik bağlam üretin ve gerekirse düzenleyin.
                    </p>
                  </div>
                  <button
                    onClick={handleGenerateContext}
                    disabled={contextLoading || loadingLessons || loadingRecord}
                    className="inline-flex items-center gap-2 px-4 py-3 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-70"
                  >
                    {contextLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <BookOpen className="w-4 h-4" />}
                    Bağlam Oluştur
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Üretilen Bağlam</label>
                    <textarea
                      rows={10}
                      value={contextText}
                      onChange={(event) => setContextText(event.target.value)}
                      className="w-full rounded-2xl border border-gray-300 px-4 py-4 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      placeholder="Bağlam burada görünecek."
                    />
                  </div>
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700 mb-2">Bağlam Değerlendirmesi</div>
                    <div className="text-sm text-emerald-900 whitespace-pre-wrap">
                      {contextRationale || "Bağlam değerlendirmesi henüz oluşmadı."}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            {currentStep === 2 ? (
              <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm space-y-5">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">
                      {isPdfMode ? "Adım 2: PDF Soru Dönüştürücü" : "Adım 2: Soru Üretici"}
                    </h2>
                    <p className="mt-1 text-sm text-gray-600">
                      {isPdfMode
                        ? pdfRewriteMode === "contextual"
                          ? "Yüklediğiniz PDF içindeki soruları tek tek okuyup aynı kazanımı koruyan, ancak denklem ve veri kurgusunu değiştirerek yeni çoklu sorular üretir."
                          : "Yüklediğiniz PDF içindeki soruları tek tek okuyup denklemi koruyan, sadece sayılarını ve yüzey anlatımını yenileyen çoklu sorular üretir."
                        : "Bağlamdan tek soru üretebilir ya da yüklediğiniz soru görselini analiz ettirip TYMM/Maarif modeline uygun yeni bir sürüm oluşturabilirsiniz."}
                    </p>
                  </div>
                  <button
                    onClick={
                      questionMode === "context"
                        ? handleGenerateQuestion
                        : questionMode === "image"
                          ? handleGenerateQuestionFromImage
                          : handleGenerateQuestionsFromPdf
                    }
                    disabled={questionLoading || !canUseQuestionMode(questionMode)}
                    title={!canUseQuestionMode(questionMode) ? "Bu modu kullanma yetkiniz yok." : undefined}
                    className="inline-flex items-center gap-2 px-4 py-3 rounded-xl bg-sky-600 text-white text-sm font-semibold hover:bg-sky-700 disabled:opacity-70"
                  >
                    {questionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    {questionLoading && isPdfMode
                      ? pdfJobStageLabel
                      : questionMode === "context"
                        ? "Soru Üret"
                        : questionMode === "image"
                          ? "Analiz Et ve Yeniden Oluştur"
                          : getPdfModeActionLabel(questionMode)}
                  </button>
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => switchQuestionMode("context")}
                    disabled={questionLoading}
                    className={`px-4 py-2 rounded-xl text-sm font-medium border transition ${
                      questionMode === "context"
                        ? "border-sky-400 bg-sky-50 text-sky-900"
                        : "border-gray-200 bg-white text-gray-700"
                    } disabled:cursor-not-allowed disabled:opacity-60`}
                  >
                    Bağlamdan Üret
                  </button>
                  <button
                    onClick={() => switchQuestionMode("image")}
                    disabled={questionLoading}
                    className={`px-4 py-2 rounded-xl text-sm font-medium border transition ${
                      questionMode === "image"
                        ? "border-sky-400 bg-sky-50 text-sky-900"
                        : "border-gray-200 bg-white text-gray-700"
                    } disabled:cursor-not-allowed disabled:opacity-60`}
                  >
                    Soru Görselinden Yeniden Oluştur
                  </button>
                  {canConvertPdfSurface ? (
                    <button
                      onClick={() => switchQuestionMode("pdf")}
                      disabled={questionLoading}
                      className={`px-4 py-2 rounded-xl text-sm font-medium border transition ${
                        questionMode === "pdf"
                          ? "border-sky-400 bg-sky-50 text-sky-900"
                          : "border-gray-200 bg-white text-gray-700"
                      } disabled:cursor-not-allowed disabled:opacity-60`}
                    >
                      PDF'ten Çoklu Soru Çevir
                    </button>
                  ) : null}
                  {canConvertPdfContextual ? (
                    <button
                      onClick={() => switchQuestionMode("pdf_contextual")}
                      disabled={questionLoading}
                      className={`px-4 py-2 rounded-xl text-sm font-medium border transition ${
                        questionMode === "pdf_contextual"
                          ? "border-sky-400 bg-sky-50 text-sky-900"
                          : "border-gray-200 bg-white text-gray-700"
                      } disabled:cursor-not-allowed disabled:opacity-60`}
                    >
                      PDF'ten Çoklu Soru Çevir Bağlamını Değiştirerek
                    </button>
                  ) : null}
                </div>

                {questionMode === "image" ? (
                  <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_320px] gap-4">
                    <div className="rounded-2xl border border-sky-200 bg-sky-50/40 p-5">
                      <div className="text-sm font-semibold text-sky-900">Yüklenen Soru Akışı</div>
                      <div className="mt-2 text-sm text-sky-900/80">
                        Sistem yüklediğiniz soru görselini önce analiz eder, eksiklerini çıkarır, sonra bu soruyu TYMM/Maarif modeline
                        uygun olacak şekilde yeniden kurar. Destek görseli istenirse, yüklenen soru ekran görüntüsünü kopyalamaz;
                        sorunun mantığına göre yeni, metinsiz ve soruya özel bir eğitim görseli üretir.
                      </div>
                      <div className="mt-4 rounded-xl bg-white border border-sky-100 p-4 text-sm text-gray-700">
                        <div>Ders, sınıf, kazanım ve süreç bileşeni görüntüden otomatik çözümlenir.</div>
                        <div className="mt-1">Bu bilgiler bu modda ayrıca modele gönderilmez.</div>
                        <div className="mt-1">Zorluk da görüntüdeki soruya göre otomatik korunur.</div>
                      </div>
                    </div>

                    <div className="rounded-2xl border border-sky-200 bg-sky-50/50 p-4">
                      <label className="block text-sm font-medium text-sky-900 mb-2">Soru Görseli</label>
                      <label className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-sky-300 bg-white px-4 py-6 text-center cursor-pointer hover:bg-sky-50">
                        <FileImage className="w-8 h-8 text-sky-500" />
                        <div className="text-sm font-medium text-sky-900">PNG, JPG veya WebP yükle</div>
                        <div className="text-xs text-sky-700/80">Metinli soru ekran görüntüsü veya soru görseli yükleyebilirsiniz.</div>
                        <input type="file" accept="image/*" className="hidden" onChange={handleQuestionImageChange} />
                      </label>
                      {questionImagePreview ? (
                        <div className="mt-4">
                          <img
                            src={questionImagePreview}
                            alt="Soru görseli önizleme"
                            className="w-full rounded-2xl border border-sky-200 bg-white object-contain max-h-72"
                          />
                        </div>
                      ) : null}
                    </div>
                  </div>
                ) : null}

                {isPdfMode ? (
                  <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_340px] gap-4">
                    <div className="rounded-2xl border border-sky-200 bg-sky-50/40 p-5">
                      <div className="text-sm font-semibold text-sky-900">{getPdfModeTitle(questionMode)}</div>
                      <div className="mt-2 text-sm text-sky-900/80">{getPdfModeDescription(questionMode)}</div>
                      <div className="mt-4 rounded-xl bg-white border border-sky-100 p-4 text-sm text-gray-700 space-y-1">
                        <div>Bir kayıt içinde çoklu soru olarak saklanır.</div>
                        <div>Ana listede soru sayısı ayrıca gösterilir.</div>
                        <div>
                          {pdfRewriteMode === "contextual"
                            ? "Bu modda denklem, veri ve sayısal kurgu değişebilir; kazanım ve çözüm mantığı sabit kalır, görsel eklenmez."
                            : "Bu modda denklem ve çözüm modeli korunur; sadece sayılar, isimler ve yüzey anlatımı yenilenir."}
                        </div>
                        <div>İsterseniz kişiselleştirilmiş prompt ile ton ve yazım biçimini yönlendirebilirsiniz.</div>
                      </div>
                      {pdfJob ? (
                        <div
                          className={`mt-4 rounded-xl border px-4 py-3 text-sm ${
                            pdfJob.status === "failed"
                              ? "border-red-200 bg-red-50 text-red-900"
                              : pdfJob.status === "completed"
                                ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                                : "border-sky-200 bg-white text-sky-900"
                          }`}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="font-semibold">{pdfJobStatusLabel}</div>
                            <div className="text-xs">
                              Adım {pdfJob.progress.currentStep}/{pdfJob.progress.totalSteps}
                            </div>
                          </div>
                          <div className="mt-1">{pdfJobStageLabel}</div>
                          <div className="mt-3 h-2 overflow-hidden rounded-full bg-white/70">
                            <div
                              className={`h-full transition-all duration-300 ${
                                pdfJob.status === "failed" ? "bg-red-500" : "bg-sky-500"
                              }`}
                              style={{ width: `${pdfJob.progress.percentage || 0}%` }}
                            />
                          </div>
                          <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs opacity-80">
                            <span>%{pdfJob.progress.percentage || 0}</span>
                            {pdfJob.updatedAt ? (
                              <span>Son güncelleme: {new Date(pdfJob.updatedAt).toLocaleTimeString("tr-TR")}</span>
                            ) : null}
                          </div>
                        </div>
                      ) : null}
                      {pdfSummary ? (
                        <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
                          <div className="font-semibold">{pdfSummary.questionCount} soru üretildi.</div>
                          {pdfSummary.notes ? <div className="mt-1 text-emerald-800/80">{pdfSummary.notes}</div> : null}
                        </div>
                      ) : null}
                    </div>

                    <div className="rounded-2xl border border-sky-200 bg-sky-50/50 p-4">
                      <label className="block text-sm font-medium text-sky-900 mb-2">Soru PDF'i</label>
                      <label
                        className={`flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-sky-300 bg-white px-4 py-6 text-center hover:bg-sky-50 ${
                          questionLoading ? "pointer-events-none cursor-not-allowed opacity-70" : "cursor-pointer"
                        }`}
                      >
                        <FileText className="w-8 h-8 text-sky-500" />
                        <div className="text-sm font-medium text-sky-900">PDF yükle</div>
                        <div className="text-xs text-sky-700/80">Birden fazla soru içeren PDF dosyası yükleyebilirsiniz.</div>
                        <input
                          type="file"
                          accept="application/pdf"
                          className="hidden"
                          onChange={handlePdfFileChange}
                          disabled={questionLoading}
                        />
                      </label>
                      {pdfFileName ? (
                        <div className="mt-4 space-y-3">
                          <div className="rounded-xl border border-sky-100 bg-white px-4 py-3 text-sm text-gray-700">
                            <div className="font-medium text-gray-900">{pdfFileName}</div>
                            <div className="mt-1 text-xs text-gray-500">
                              {pdfMetadataLoading
                                ? "Sayfa bilgileri okunuyor..."
                                : pdfPageCount
                                  ? `${pdfPageCount} sayfa • ${selectedPdfPages.length} seçili`
                                  : "Sayfa bilgisi henüz alınmadı"}
                            </div>
                          </div>
                          <button
                            onClick={openPdfPageModal}
                            disabled={!pdfFile || pdfMetadataLoading || questionLoading}
                            className="w-full rounded-xl border border-sky-200 bg-white px-4 py-3 text-sm font-medium text-sky-900 hover:bg-sky-50 disabled:opacity-60"
                          >
                            Sayfa Seçimini Düzenle
                          </button>
                        </div>
                      ) : null}
                    </div>
                  </div>
                ) : null}

                {questionSourceAnalysis ? (
                  <div className="rounded-2xl border border-sky-200 bg-sky-50/50 p-5 space-y-4">
                    <div>
                      <div className="text-lg font-semibold text-gray-900">Kaynak Soru Analizi</div>
                      <div className="mt-2 rounded-xl bg-white border border-sky-100 p-4 text-sm text-gray-700 whitespace-pre-wrap">
                        {questionSourceAnalysis.originalQuestionSummary || "Kaynak soru özeti oluşturulamadı."}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                      <div className="rounded-xl bg-white border border-emerald-100 p-4">
                        <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700 mb-2">Güçlü Yanlar</div>
                        <div className="space-y-2 text-sm text-gray-700">
                          {questionSourceAnalysis.strengths.length
                            ? questionSourceAnalysis.strengths.map((item, index) => <div key={index}>• {item}</div>)
                            : <div>-</div>}
                        </div>
                      </div>
                      <div className="rounded-xl bg-white border border-rose-100 p-4">
                        <div className="text-xs font-semibold uppercase tracking-wide text-rose-700 mb-2">Eksikler</div>
                        <div className="space-y-2 text-sm text-gray-700">
                          {questionSourceAnalysis.weaknesses.length
                            ? questionSourceAnalysis.weaknesses.map((item, index) => <div key={index}>• {item}</div>)
                            : <div>-</div>}
                        </div>
                      </div>
                      <div className="rounded-xl bg-white border border-amber-100 p-4">
                        <div className="text-xs font-semibold uppercase tracking-wide text-amber-700 mb-2">Yeniden Kurma Odağı</div>
                        <div className="space-y-2 text-sm text-gray-700">
                          {questionSourceAnalysis.improvementFocus.length
                            ? questionSourceAnalysis.improvementFocus.map((item, index) => <div key={index}>• {item}</div>)
                            : <div>-</div>}
                        </div>
                        <div className="mt-4 rounded-lg bg-amber-50 border border-amber-100 px-3 py-2 text-xs text-amber-900">
                          {questionSourceAnalysis.detectedVisualNeed
                            ? `Görsel destek yeniden üretildi. ${questionSourceAnalysis.visualRationale || ""}`
                            : "Kaynak soruda ayrı bir destek görseli zorunlu görülmedi."}
                        </div>
                      </div>
                    </div>

                    <div className="rounded-xl bg-white border border-sky-100 p-4">
                      <div className="text-xs font-semibold uppercase tracking-wide text-sky-700 mb-2">Nasıl İyileştirdik</div>
                      <div className="space-y-2 text-sm text-gray-700">
                        {questionSourceAnalysis.howWeImproved.length
                          ? questionSourceAnalysis.howWeImproved.map((item, index) => <div key={index}>• {item}</div>)
                          : <div>-</div>}
                      </div>
                    </div>
                  </div>
                ) : null}

                {isPdfMode ? (
                  <QuestionCollectionPreview title={getPdfModePreviewTitle(questionMode)} questions={multiQuestions} />
                ) : (
                  <QuestionPreviewCard title="Üretilen Soru" question={question} />
                )}
              </div>
            ) : null}

            {currentStep === 3 && !isPdfMode ? (
              <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm space-y-5">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">Adım 3: Soru Denetmeni</h2>
                    <p className="mt-1 text-sm text-gray-600">
                      Üretilen soruyu denetleyin ya da dışarıdan bir soru metni/görseli yükleyip rapor alın. Rapor sonrası aynı konu mantığında yeniden üretim yapabilirsiniz.
                    </p>
                  </div>
                  <button
                    onClick={handleAudit}
                    disabled={auditLoading}
                    className="inline-flex items-center gap-2 px-4 py-3 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:opacity-70"
                  >
                    {auditLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckSquare className="w-4 h-4" />}
                    Denetim Raporu Üret
                  </button>
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setAuditMode("generated")}
                    disabled={!generatedQuestionForAudit}
                    className={`px-4 py-2 rounded-xl text-sm font-medium border transition ${
                      auditMode === "generated"
                        ? "border-violet-400 bg-violet-50 text-violet-900"
                        : "border-gray-200 bg-white text-gray-700"
                    } disabled:opacity-50`}
                  >
                    Sihirbaz Sorusunu Denetle
                  </button>
                  <button
                    onClick={() => setAuditMode("external")}
                    className={`px-4 py-2 rounded-xl text-sm font-medium border transition ${
                      auditMode === "external"
                        ? "border-violet-400 bg-violet-50 text-violet-900"
                        : "border-gray-200 bg-white text-gray-700"
                    }`}
                  >
                    Dışarıdan Soru Denetle
                  </button>
                </div>

                {auditMode === "generated" ? (
                  <div className="rounded-2xl border border-violet-200 bg-violet-50/60 p-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-violet-700 mb-2">Denetlenecek İçerik</div>
                    <div className="rounded-xl bg-white border border-gray-200 p-4 text-sm text-gray-700 whitespace-pre-wrap">
                      {generatedQuestionText || "Henüz denetlenecek sihirbaz sorusu yok."}
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_280px] gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Soru Metni</label>
                      <textarea
                        rows={10}
                        value={externalQuestionText}
                        onChange={(event) => setExternalQuestionText(event.target.value)}
                        className="w-full rounded-2xl border border-gray-300 px-4 py-4 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-violet-500"
                        placeholder="Bağlam, soru kökü ve şıkları buraya yapıştırın."
                      />
                    </div>
                    <div className="rounded-2xl border border-violet-200 bg-violet-50/50 p-4">
                      <label className="block text-sm font-medium text-violet-900 mb-2">Soru Görseli</label>
                      <label className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-violet-300 bg-white px-4 py-6 text-center cursor-pointer hover:bg-violet-50">
                        <FileImage className="w-8 h-8 text-violet-500" />
                        <div className="text-sm font-medium text-violet-900">PNG, JPG veya WebP yükle</div>
                        <div className="text-xs text-violet-700/80">Kalıcı saklanmaz, sadece denetim için kullanılır.</div>
                        <input type="file" accept="image/*" className="hidden" onChange={handleAuditImageChange} />
                      </label>
                      {auditImagePreview ? (
                        <div className="mt-4">
                          <img
                            src={auditImagePreview}
                            alt="Denetim görseli"
                            className="w-full rounded-2xl border border-violet-200 bg-white object-contain max-h-64"
                          />
                        </div>
                      ) : null}
                    </div>
                  </div>
                )}

                {auditReport ? (
                  <div className="rounded-2xl border border-violet-200 bg-violet-50/50 p-5 space-y-4">
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                      <div>
                        <div className="text-lg font-semibold text-gray-900">TYMM / Maarif Uyum Raporu</div>
                        <div className="text-sm text-violet-900/80 mt-1">{auditReport.alignmentVerdict}</div>
                      </div>
                      <button
                        onClick={handleRewrite}
                        disabled={rewriteLoading}
                        className="inline-flex items-center gap-2 px-4 py-3 rounded-xl bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600 disabled:opacity-70"
                      >
                        {rewriteLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                        Yeniden Üret
                      </button>
                    </div>

                    <div className="rounded-xl bg-white border border-violet-100 p-4 text-sm text-gray-700 whitespace-pre-wrap">
                      {auditReport.summary}
                    </div>

                    <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                      <div className="rounded-xl bg-white border border-emerald-100 p-4">
                        <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700 mb-2">İyi Yönler</div>
                        <div className="space-y-2 text-sm text-gray-700">
                          {auditReport.strengths.length ? auditReport.strengths.map((item, index) => <div key={index}>• {item}</div>) : <div>-</div>}
                        </div>
                      </div>
                      <div className="rounded-xl bg-white border border-rose-100 p-4">
                        <div className="text-xs font-semibold uppercase tracking-wide text-rose-700 mb-2">Zayıf Yönler</div>
                        <div className="space-y-2 text-sm text-gray-700">
                          {auditReport.weaknesses.length ? auditReport.weaknesses.map((item, index) => <div key={index}>• {item}</div>) : <div>-</div>}
                        </div>
                      </div>
                      <div className="rounded-xl bg-white border border-amber-100 p-4">
                        <div className="text-xs font-semibold uppercase tracking-wide text-amber-700 mb-2">İyileştirme Önerileri</div>
                        <div className="space-y-2 text-sm text-gray-700">
                          {auditReport.improvementSuggestions.length
                            ? auditReport.improvementSuggestions.map((item, index) => <div key={index}>• {item}</div>)
                            : <div>-</div>}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}

                <QuestionPreviewCard title="Yeniden Üretilen Soru" question={rewriteQuestion} tone="amber" />
              </div>
            ) : null}
          </div>
        </div>

        <PdfPageSelectModal
          isOpen={pdfPageModalOpen}
          onClose={() => setPdfPageModalOpen(false)}
          pageCount={pdfPageCount}
          file={pdfFile}
          fileName={pdfFileName}
          initialSelectedPages={selectedPdfPages}
          onConfirm={(pages) => {
            setSelectedPdfPages(pages);
            setPdfPageModalOpen(false);
          }}
        />
      </div>
  );
}
